import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Translation } from '../../../_languages/translations';
import { Globals } from '../../../_globals/globals';
import { FileService } from '../../../file/file.service';
import { OptiService } from '../../../opti/opti.service';
import { CuttingTableService } from '../../../maestros/cutting_table/cutting_table.service';
import { BendingMachineService } from '../../../maestros/bending_machine/bending_machine.service';
import { EaselService } from '../../../maestros/easel/easel.service';
import { Easel } from '../../../_models/easel';

@Component({
  selector: 'app-auto-opti-show',
  templateUrl: './auto-opti-show.component.html'
})
export class AutoOptiShowComponent implements OnInit, OnDestroy {
  tr: {};

  title: string;
  subtitle: string;

  lotNumber = '';
  loadingMaterials = false;
  runningBatch = false;
  errorMessage = '';
  infoMessage = '';
  executionProgress = '';
  runningRealBatch = false;
  autoRunLiveVisible = false;
  autoRunLiveCurrent = 0;
  autoRunLiveTotal = 0;
  autoRunLivePercent = 0;
  autoRunLivePulse = 0;
  autoRunLiveStalled = false;
  autoRunLiveCurrentMaterial = '';
  autoRunLiveElapsedText = '00:00';
  autoRunLiveHeartbeatText = '-';
  autoRunLiveStatusText = '';
  autoRunLiveTimer: any = null;
  loadingLastSummary = false;
  floorPickerModal = false;
  floorPickerLotFilter = '';
  loadingFloorPicker = false;
  floorPickerRows: any[] = [];
  cuttingTables: any[] = [];
  bendingMachines: any[] = [];
  selectedBendingMachineId: number = null;
  easelList: Easel[] = [];
  selectedCompactEaselId: number = null;

  materialRows: any[] = [];
  resultRows: any[] = [];
  floorPiecesByMaterial: any = {};
  autoRowsById: any = {};
  selectedLots: string[] = [];
  bulkPdfStateKey = 'vs_auto_bulk_pdf_state';
  bulkProgressVisible = false;
  bulkProgressCurrent = 0;
  bulkProgressTotal = 0;
  bulkProgressLabel = '';
  printingCompactSummary = false;
  compactSummaryMode = 'classic';
  profileSummaryIncludeGrouped = true;
  compactStacksTotal: number = null;
  resultSortField = 'pieces_count';
  resultSortAsc = false;
  resultPage = 1;
  resultPageSize = 10;
  historicalPdfWorkerFrameId = 'vs-auto-pdf-worker-frame';
  bulkPulseFrame = 0;
  bulkStalled = false;
  bulkStallMs = 90000;
  bulkHeartbeatTimer: any = null;
  bulkLastStepTs = 0;
  bulkCncRunning = false;
  bulkCncIds: number[] = [];
  bulkCncIndex = 0;
  bulkCncFilesDownloaded = 0;
  bulkCncCollectedFiles: any[] = [];
  bulkCncAutoRowLoadPromises: any = {};
  warningsModal = false;
  warningsModalTitle = '';
  warningsModalItems: string[] = [];
  bendingCodesModal = false;
  bendingCodesMissingRows: any[] = [];
  bendingCodesLotList: string[] = [];
  bendingCodesMachineId: number = null;
  helpModal = false;
  helpSection = 'overview';
  tourModal = false;
  tourIndex = 0;
  deletingWarningOptis = false;
  private pendingFloorPiecesRequest: any = null;
  private pendingLastSummaryRequest: any = null;
  private pendingFloorPickerRequest: any = null;
  private beforeUnloadListener = (event: BeforeUnloadEvent) => {
    if (!this.hasActiveAutoProcess()) {
      return;
    }
    const msg = 'Hay una optimización automática en curso. Si recarga o cierra, puede interrumpir el proceso.';
    event.preventDefault();
    event.returnValue = msg;
    return msg;
  };
  private ignoreHistoricalPdfResults = false;
  private ignoreHistoricalPdfResultsTimer: any = null;
  private pendingRowPdfOpen: { optiId: number, type: string } = null;

  private storageListener = (event: StorageEvent) => {
    if (!event || !event.key) {
      return;
    }

    if (event.key === 'vs_auto_historical_pdf_result' && event.newValue) {
      this.consumeHistoricalPdfResult();
    } else if (event.key === this.bulkPdfStateKey) {
      this.syncBulkProgressFromState();
    }
  };

  private isTopWindow(): boolean {
    return window.self === window.top;
  }

  constructor(
    public trans: Translation,
    private fileService: FileService,
    private optiService: OptiService,
    private cuttingTableService: CuttingTableService,
    private bendingMachineService: BendingMachineService,
    private easelService: EaselService,
    private globals: Globals,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.tr = this.trans.vocabulary;
    this.title = 'Optimización automática por lote';
    this.subtitle = 'Carga por material desde Floor';
    this.cleanupStaleAutoContext();
    if (!this.hasPendingAutoExecutionContext()) {
      this.clearTransientAutoState();
    }
    this.restoreHistoricalSummaryState();
    this.loadCuttingTables();
    this.loadBendingMachines();
    this.loadEasels();
    this.syncBulkProgressFromState();
    if (this.isTopWindow()) {
      this.consumeHistoricalPdfResult();
    }
    this.resumeRealBatchIfNeeded();
    this.applyEntryQueryParams();
    this.startAutoRunLiveLoop();
    window.addEventListener('storage', this.storageListener);
    window.addEventListener('beforeunload', this.beforeUnloadListener);
  }

  private applyEntryQueryParams() {
    const lotParam = this.route && this.route.snapshot && this.route.snapshot.queryParamMap
      ? this.route.snapshot.queryParamMap.get('lot')
      : null;
    const actionParam = this.route && this.route.snapshot && this.route.snapshot.queryParamMap
      ? this.route.snapshot.queryParamMap.get('action')
      : null;

    const lot = lotParam ? lotParam.toString().trim() : '';
    if (!/^[0-9]+$/.test(lot)) {
      return;
    }

    this.lotNumber = lot;

    const action = actionParam ? actionParam.toString().trim().toLowerCase() : '';
    if (action === 'recover') {
      this.loadLastSummaryByLot();
      return;
    }
    if (action === 'start') {
      this.loadMaterialsFromFloor();
    }
  }

  ngOnDestroy() {
    window.removeEventListener('storage', this.storageListener);
    window.removeEventListener('beforeunload', this.beforeUnloadListener);
    this.stopAutoRunLiveLoop();
    this.stopBulkHeartbeat();
    this.cancelPendingRequests();
    this.clearIgnoreHistoricalPdfResultsTimer();
    const frame = document.getElementById(this.historicalPdfWorkerFrameId);
    if (frame && frame.parentNode) {
      frame.parentNode.removeChild(frame);
    }
  }

  resetAutoScreen() {
    this.errorMessage = '';
    this.infoMessage = '';
    this.hardResetAutoContext();
    this.resetBulkCncState();
    this.clearTransientAutoState();
    this.resultPage = 1;
    this.executionProgress = '';
    this.closeWarningsModal();
  }

  canEmergencyStop(): boolean {
    return this.hasActiveAutoProcess();
  }

  emergencyStop() {
    this.errorMessage = '';
    const confirmText = 'Se cancelará el proceso en curso (optimización automática / generación PDF-CNC). ¿Desea continuar?';
    if (!window.confirm(confirmText)) {
      return;
    }

    this.cancelActiveAutoFlows();
    this.infoMessage = 'Parada de emergencia activada. Proceso cancelado.';
  }

  openWarningsModal(rowIn: any) {
    const warnings = rowIn && rowIn.warnings && Array.isArray(rowIn.warnings)
      ? rowIn.warnings.filter(item => item !== null && item !== undefined && item.toString().trim() !== '')
      : [];

    if (warnings.length === 0) {
      this.warningsModal = false;
      this.warningsModalTitle = '';
      this.warningsModalItems = [];
      return;
    }

    const materialLabel = rowIn && rowIn.materialid
      ? (rowIn.materialid.toString() + ' - ' + (rowIn.materialdesc ? rowIn.materialdesc.toString() : ''))
      : 'Material';
    const optiLabel = rowIn && rowIn.opti_id ? rowIn.opti_id.toString() : '-';

    this.warningsModalTitle = 'Avisos | Opti ' + optiLabel + ' | ' + materialLabel;
    this.warningsModalItems = warnings.map(item => item.toString());
    this.warningsModal = true;
  }

  closeWarningsModal() {
    this.warningsModal = false;
    this.warningsModalTitle = '';
    this.warningsModalItems = [];
  }

  showHelpModal(valueIn: boolean) {
    this.helpModal = valueIn;
    if (valueIn) {
      this.helpSection = 'overview';
    }
  }

  setHelpSection(sectionIn: string) {
    if (!sectionIn) {
      return;
    }
    this.helpSection = sectionIn;
  }

  isHelpSection(sectionIn: string): boolean {
    return this.helpSection === sectionIn;
  }

  getTourSteps(): Array<{title: string, body: string, targetId: string}> {
    const hasConfig = !!(this.materialRows && this.materialRows.length > 0);
    const hasSummary = !!(this.resultRows && this.resultRows.length > 0);

    if (!hasConfig) {
      return [
        {
          title: '1) Carga de lote',
          body: 'Introduce uno o varios lotes y usa Comenzar AO para preparar la optimización.',
          targetId: 'auto-tour-lot'
        },
        {
          title: '2) Acciones disponibles',
          body: 'Puedes iniciar AO, recuperar ejecuciones previas o abrir ayuda. Para el tour completo, primero carga materiales/resultados.',
          targetId: 'auto-tour-lot'
        }
      ];
    }

    const steps: Array<{title: string, body: string, targetId: string}> = [
      {
        title: '1) Carga de lote',
        body: 'Introduce uno o varios lotes y usa Comenzar AO para preparar la configuración por material.',
        targetId: 'auto-tour-lot'
      },
      {
        title: '2) Configuración por material',
        body: 'Revisa mesa de corte, estrategia, medidas de plancha y modo de optimización antes de ejecutar.',
        targetId: 'auto-tour-config'
      },
      {
        title: '3) Validación y ejecución',
        body: 'Primero valida configuración y después ejecuta la optimización real para guardar resultados y documentos.',
        targetId: 'auto-tour-validate'
      }
    ];

    if (hasSummary) {
      steps.push(
        {
          title: '4) Revisión de resultados',
          body: 'En Resumen preliminar revisa estado, merma, pilas y tiempos. Usa Ver cortes y enlaces PDF.',
          targetId: 'auto-tour-summary'
        },
        {
          title: '5) Salidas',
          body: 'Genera listado de compactación, resumen, descarga masiva de PDFs y CNCs según necesidad de planta.',
          targetId: 'auto-tour-outputs'
        }
      );
    } else {
      steps.push({
        title: '4) Siguiente bloque',
        body: 'Cuando ejecutes AO y aparezca el resumen preliminar, el tour añadirá automáticamente pasos de revisión y salidas.',
        targetId: 'auto-tour-validate'
      });
    }

    return steps;
  }

  getCurrentTourStep(): {title: string, body: string, targetId: string} {
    const steps = this.getTourSteps();
    if (!steps || steps.length === 0) {
      return { title: '', body: '', targetId: '' };
    }
    const index = Math.max(0, Math.min(this.tourIndex, steps.length - 1));
    return steps[index];
  }

  startTour() {
    this.helpModal = false;
    this.tourIndex = 0;
    this.tourModal = true;
    this.focusTourTarget();
  }

  closeTour() {
    this.tourModal = false;
  }

  nextTourStep() {
    const steps = this.getTourSteps();
    if (this.tourIndex < (steps.length - 1)) {
      this.tourIndex++;
      this.focusTourTarget();
      return;
    }
    this.closeTour();
  }

  prevTourStep() {
    if (this.tourIndex > 0) {
      this.tourIndex--;
      this.focusTourTarget();
    }
  }

  private focusTourTarget() {
    const step = this.getCurrentTourStep();
    const targetId = step && step.targetId ? step.targetId : '';
    if (!targetId) {
      return;
    }

    setTimeout(() => {
      const element = document.getElementById(targetId);
      if (!element) {
        return;
      }

      try {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (error) {
        element.scrollIntoView();
      }

      const previousOutline = (element as HTMLElement).style.outline;
      const previousOffset = (element as HTMLElement).style.outlineOffset;
      (element as HTMLElement).style.outline = '3px solid #3298dc';
      (element as HTMLElement).style.outlineOffset = '4px';
      setTimeout(() => {
        (element as HTMLElement).style.outline = previousOutline;
        (element as HTMLElement).style.outlineOffset = previousOffset;
      }, 900);
    }, 80);
  }

  getWarningOptiCount(): number {
    if (!this.resultRows || this.resultRows.length === 0) {
      return 0;
    }
    return this.resultRows.filter(row =>
      row &&
      row.opti_id &&
      row.opti_id !== '-' &&
      row.warning_count &&
      Number(row.warning_count) > 0
    ).length;
  }

  getBatchOptiCount(): number {
    if (!this.resultRows || this.resultRows.length === 0) {
      return 0;
    }
    return this.resultRows.filter(row => row && row.opti_id && row.opti_id !== '-').length;
  }

  async deleteBatchOptimizations() {
    this.errorMessage = '';
    this.infoMessage = '';

    if (this.deletingWarningOptis) {
      return;
    }

    const batchRows = (this.resultRows || []).filter(row =>
      row &&
      row.opti_id &&
      row.opti_id !== '-'
    );
    const warningRows = batchRows.filter(row => row.warning_count && Number(row.warning_count) > 0);

    if (batchRows.length === 0) {
      this.infoMessage = 'No hay optimizaciones para borrar.';
      return;
    }

    const answer = window.confirm(
      'Se van a borrar ' + batchRows.length + ' optimización(es) del bloque actual (' + warningRows.length + ' con error/aviso). Esta acción no se puede deshacer. ¿Continuar?'
    );
    if (!answer) {
      return;
    }

    this.deletingWarningOptis = true;
    this.bulkProgressVisible = true;
    this.bulkProgressCurrent = 0;
    this.bulkProgressTotal = batchRows.length;
    this.bulkProgressLabel = 'Borrando bloque de optimizaciones...';

    let deleted = 0;
    const failedIds: number[] = [];

    for (let i = 0; i < batchRows.length; i++) {
      const row = batchRows[i];
      const optiId = Number(row.opti_id);
      this.infoMessage = 'Borrando optimización ' + optiId + ' (' + (i + 1) + '/' + batchRows.length + ')...';

      const ok = await this.deleteSingleOptimization(optiId);
      if (ok) {
        deleted += 1;
      } else {
        failedIds.push(optiId);
      }

      this.bulkProgressCurrent = i + 1;
    }

    this.bulkProgressVisible = false;
    this.deletingWarningOptis = false;

    if (deleted > 0) {
      const failedSet = new Set(failedIds);
      this.resultRows = this.resultRows.filter(row => {
        const idNum = row && row.opti_id ? Number(row.opti_id) : 0;
        const wasDeletedCandidate = batchRows.find(w => Number(w.opti_id) === idNum);
        if (!wasDeletedCandidate) {
          return true;
        }
        return failedSet.has(idNum);
      });
      batchRows.forEach(row => {
        const idNum = row && row.opti_id ? Number(row.opti_id) : 0;
        if (!failedSet.has(idNum) && this.autoRowsById && this.autoRowsById[idNum]) {
          delete this.autoRowsById[idNum];
        }
      });
      this.resultPage = 1;
      this.persistHistoricalSummaryState();
    }

    if (failedIds.length > 0) {
      this.errorMessage = 'Se borraron ' + deleted + ' optimizaciones del bloque. Fallaron: ' + failedIds.join(', ') + '.';
      return;
    }

    this.infoMessage = 'Borradas correctamente ' + deleted + ' optimizaciones del bloque (' + warningRows.length + ' con error/aviso).';
  }

  private deleteSingleOptimization(optiIdIn: number): Promise<boolean> {
    if (!optiIdIn || isNaN(optiIdIn) || optiIdIn <= 0) {
      return Promise.resolve(false);
    }

    return new Promise(resolve => {
      this.optiService.deleteOpti({ id: optiIdIn } as any).subscribe(
        () => resolve(true),
        () => resolve(false)
      );
    });
  }

  showFloorPicker(valueIn: boolean) {
    this.floorPickerModal = valueIn;
    if (valueIn) {
      this.floorPickerLotFilter = this.lotNumber ? this.lotNumber : '';
      this.floorPickerRows = [];
      this.searchFloorPicker();
    }
  }

  searchFloorPicker() {
    this.loadingFloorPicker = true;
    const params = {
      f_broke: false,
      f_finished: true,
      f_batchno: this.floorPickerLotFilter ? this.floorPickerLotFilter.trim() : '',
      f_barcode: null,
      f_mat: null,
      f_width: null,
      f_height: null,
      offset: 1,
      pagesize: 5000
    };

    this.cancelSubscription('pendingFloorPickerRequest');
    this.pendingFloorPickerRequest = this.fileService.getPiece(params).subscribe(
      (response: any) => {
        this.pendingFloorPickerRequest = null;
        this.loadingFloorPicker = false;
        this.floorPickerRows = this.buildFloorPickerRows(response);
      },
      () => {
        this.pendingFloorPickerRequest = null;
        this.loadingFloorPicker = false;
        this.floorPickerRows = [];
      }
    );
  }

  selectFloorPickerRow(rowIn: any) {
    if (!rowIn || !rowIn.batchno) {
      return;
    }
    this.lotNumber = rowIn.batchno.toString();
    this.floorPickerLotFilter = this.lotNumber;
    this.floorPickerModal = false;
    this.infoMessage = 'Lote ' + this.lotNumber + ' seleccionado desde Floor. Cargando materiales...';
    this.loadMaterialsFromFloor();
  }

  loadLastSummaryByLot() {
    this.errorMessage = '';
    this.infoMessage = '';
    this.executionProgress = '';

    const lot = this.lotNumber ? this.lotNumber.trim() : '';
    if (!lot) {
      this.errorMessage = 'Debe indicar un número de lote para recuperar un resumen previo.';
      return;
    }
    if (lot.indexOf(',') !== -1) {
      this.errorMessage = 'La carga de resumen histórico sólo está disponible para un lote individual.';
      return;
    }
    this.loadingLastSummary = true;
    this.compactStacksTotal = null;

    const params = {
      filterField_0: '',
      filterField_1: '',
      filterField_2: '',
      filterField_3: '',
      opti_id: '',
      opti_lot: lot,
      opti_mat: '',
      opti_note: 'Opt. Auto',
      groupId: localStorage.getItem('group_id'),
      type: 0,
      f_date: null,
      f_date2: null
    };

    this.cancelSubscription('pendingLastSummaryRequest');
    this.pendingLastSummaryRequest = this.optiService.getAllOpti(params).subscribe(
      (response: any[]) => {
        this.pendingLastSummaryRequest = null;
        this.loadingLastSummary = false;

        if (!response || response.length === 0) {
          this.errorMessage = 'No se han encontrado optimizaciones automáticas para ese lote en base de datos.';
          return;
        }

        const autoRows = response.filter(item =>
          item &&
          item.od_lots_number &&
          item.od_notes &&
          item.od_lots_number.toString().indexOf(lot) !== -1 &&
          item.od_notes.toString().indexOf('Opt. Auto') === 0
        );

        if (!autoRows || autoRows.length === 0) {
          this.errorMessage = 'No se han encontrado ejecuciones automáticas válidas para ese lote.';
          return;
        }

        const latestRows = this.selectLatestRowsByMaterial(autoRows);

        if (!latestRows || latestRows.length === 0) {
          this.errorMessage = 'No se ha podido reconstruir la última ejecución automática del lote.';
          return;
        }

        this.resultRows = latestRows.map(item => {
          const lotsDetectedRaw = this.extractLotsFromHistorical(item && item.od_lots_number ? item.od_lots_number : '');
          const lotsDetected = lotsDetectedRaw.length > 0 ? lotsDetectedRaw : [lot];
          const historicalTime = this.getHistoricalOptimizationTime(item);
          const tableInfo = this.extractCuttingTableInfo(item.od_state);
          return {
            materialid: this.extractMaterialId(item.od_product_id),
            materialdesc: this.extractMaterialDesc(item.od_product_desc),
            cutting_table_id: tableInfo.id,
            cutting_table_name: this.resolveCuttingTableName(tableInfo.id, tableInfo.name),
            level: this.extractLevelFromConfiguration(item.od_configuration),
            status: 'RECUPERADA',
            strategy: this.formatStrategyLabel(this.extractStrategy(item.od_configuration)),
            speed: '-',
            opti_id: item.id ? item.id : '-',
            sheets_used: item.od_stock_plates_number ? item.od_stock_plates_number : '-',
            stacks: item.od_total_easels ? item.od_total_easels : '-',
            waste: (item.od_porcentage_wastage || item.od_porcentage_wastage === 0) ? Number(item.od_porcentage_wastage).toFixed(2) + '%' : '-',
            a_cola: this.getHistoricalACola(item),
            a_resto: this.calculateHistoricalAResto(item),
            optimization_time: historicalTime.text,
            optimization_time_seconds: historicalTime.seconds,
            pdf_groups_section: '',
            pdf_cutting_section: '',
            pdf_graphics_section: '',
            historical: true,
            can_load_pdf: true,
            lots_label: this.getLotsUsageLabel(lotsDetected, lotsDetected),
            pieces_count: item.od_total_pieces ? item.od_total_pieces : 0,
            units_total: item.od_total_pieces ? Number(item.od_total_pieces) : 0,
            area_total_m2: item.od_net_area ? Number(item.od_net_area) : 0
          };
        });

        this.autoRowsById = {};
        latestRows.forEach(item => {
          if (item && item.id) {
            this.autoRowsById[item.id] = item;
          }
        });

        this.resultPage = 1;
        this.consumeHistoricalPdfResult();
        const noteText = latestRows[0] && latestRows[0].od_notes ? latestRows[0].od_notes.toString() : 'Opt. Auto';
        this.infoMessage = 'Resumen recuperado desde BD. Ejecución: ' + noteText + ' (' + latestRows.length + ' materiales).';
        this.persistHistoricalSummaryState();
      },
      () => {
        this.pendingLastSummaryRequest = null;
        this.loadingLastSummary = false;
        this.errorMessage = 'Error al recuperar el resumen automático desde base de datos.';
      }
    );
  }

  loadMaterialsFromFloor() {
    this.errorMessage = '';
    this.infoMessage = '';
    this.resultRows = [];
    this.resultPage = 1;
    this.materialRows = [];
    this.floorPiecesByMaterial = {};
    this.executionProgress = '';
    this.compactStacksTotal = null;

    const lotList = this.parseLotNumbers(this.lotNumber);
    if (lotList.length === 0) {
      this.errorMessage = 'Debe indicar uno o varios lotes válidos (ejemplo: 146410,146411).';
      return;
    }
    this.selectedLots = lotList;

    this.loadingMaterials = true;
    this.autoRowsById = {};

    const params = {
      f_broke: false,
      f_finished: true,
      f_batchno: lotList.join(','),
      f_barcode: null,
      f_mat: null,
      f_width: null,
      f_height: null,
      offset: 1,
      pagesize: 2000
    };

    this.cancelSubscription('pendingFloorPiecesRequest');
    this.pendingFloorPiecesRequest = this.fileService.getPiece(params).subscribe(
      (response: any) => {
        this.pendingFloorPiecesRequest = null;
        this.loadingMaterials = false;
        this.materialRows = this.buildMaterialRows(response);
        this.applyDefaultCuttingTablesToRows();

        if (this.materialRows.length === 0) {
          this.errorMessage = 'No se han encontrado materiales para los lotes indicados en Floor.';
          return;
        }

        this.infoMessage = 'Materiales cargados: ' + this.materialRows.length + ' (Lotes: ' + lotList.join(', ') + ')';
      },
      () => {
        this.pendingFloorPiecesRequest = null;
        this.loadingMaterials = false;
        this.errorMessage = 'Error al cargar los materiales desde Floor.';
      }
    );
  }

  async runBatchPreview() {
    this.errorMessage = '';
    this.infoMessage = '';
    this.resultRows = [];
    this.resultPage = 1;
    this.executionProgress = '';
    this.compactStacksTotal = null;

    if (this.materialRows.length === 0) {
      this.errorMessage = 'Primero debe cargar materiales del lote.';
      return;
    }

    this.runningBatch = true;

    const resultAux = [];
    for (let i = 0; i < this.materialRows.length; i++) {
      const row = this.normalizeMaterialRow(this.materialRows[i]);
      const materialInfo = this.floorPiecesByMaterial[row.materialid];

      this.executionProgress = 'Procesando material ' + (i + 1) + ' de ' + this.materialRows.length + '...';
      await this.delay(120);

      resultAux.push({
        materialid: row.materialid,
        materialdesc: row.materialdesc,
        cutting_table_id: row.cutting_table_id ? Number(row.cutting_table_id) : null,
        cutting_table_name: this.resolveCuttingTableName(row.cutting_table_id, ''),
        level: row.level && Number(row.level) > 0 ? Number(row.level) : '-',
        status: this.hasUsableRemnant(row) ? 'Preparado (con retal)' : 'Preparado (sin retal)',
        strategy: row.strategy === 'less_stacks' ? 'menos pilas' : 'menos merma',
        speed: this.getSpeedLabel(row.speed),
        opti_id: '-',
        sheets_used: '-',
        stacks: '-',
        waste: '-',
        a_cola: '-',
        a_resto: '-',
        optimization_time: '-',
        optimization_time_seconds: 0,
        pdf_groups_section: '',
        pdf_cutting_section: '',
        pdf_graphics_section: '',
        pieces_count: materialInfo ? materialInfo.pieces_count : 0,
        units_total: materialInfo ? materialInfo.units_total : 0,
        area_total_m2: materialInfo ? materialInfo.area_total_m2 : 0,
        plan_payload: this.buildPlanPayload(row, materialInfo)
      });
    }

    this.resultRows = resultAux;
    this.resultPage = 1;
    this.runningBatch = false;
    this.executionProgress = '';
    this.infoMessage = 'Ejecución secuencial base completada. Ya están preparados los payloads por material.';
  }

  runBatchReal() {
    this.errorMessage = '';
    this.infoMessage = '';
    this.executionProgress = '';
    this.compactStacksTotal = null;

    if (this.materialRows.length === 0) {
      this.errorMessage = 'Primero debe cargar materiales del lote.';
      return;
    }

    const jobs = [];
    for (let i = 0; i < this.materialRows.length; i++) {
      const row = this.normalizeMaterialRow(this.materialRows[i]);
      if (!row.cutting_table_id || Number(row.cutting_table_id) <= 0) {
        this.errorMessage = 'Debe seleccionar una mesa de corte para todos los materiales antes de ejecutar la optimización real.';
        return;
      }
      const materialInfo = this.floorPiecesByMaterial[row.materialid];
      jobs.push(this.buildPlanPayload(row, materialInfo));
    }

    localStorage.setItem('vs_auto_batch_queue', JSON.stringify(jobs));
    localStorage.setItem('vs_auto_batch_index', '0');
    localStorage.setItem('vs_auto_batch_results', JSON.stringify([]));
    localStorage.setItem('vs_auto_batch_continue', '1');
    localStorage.removeItem('vs_auto_batch_local_hb');
    localStorage.removeItem('vs_auto_batch_backend_hb');
    localStorage.removeItem('vs_auto_batch_job_started_at');

    this.runningRealBatch = true;
    this.infoMessage = 'Iniciando ejecución real secuencial...';
    localStorage.setItem('vs_auto_batch_started_at', Date.now().toString());
    this.launchNextRealJob();
  }

  private startAutoRunLiveLoop() {
    this.refreshAutoRunLiveState();
    if (this.autoRunLiveTimer) {
      return;
    }
    this.autoRunLiveTimer = setInterval(() => {
      this.autoRunLivePulse = (this.autoRunLivePulse + 1) % 4;
      this.refreshAutoRunLiveState();
    }, 1000);
  }

  private stopAutoRunLiveLoop() {
    if (this.autoRunLiveTimer) {
      clearInterval(this.autoRunLiveTimer);
      this.autoRunLiveTimer = null;
    }
    this.autoRunLiveVisible = false;
  }

  private formatSecondsToMMSS(totalSecondsIn: number): string {
    const secs = Number.isFinite(totalSecondsIn) && totalSecondsIn > 0 ? Math.floor(totalSecondsIn) : 0;
    const mm = Math.floor(secs / 60);
    const ss = secs % 60;
    const mmTxt = mm < 10 ? ('0' + mm.toString()) : mm.toString();
    const ssTxt = ss < 10 ? ('0' + ss.toString()) : ss.toString();
    return mmTxt + ':' + ssTxt;
  }

  private formatAgeText(ageSecondsIn: number): string {
    if (!Number.isFinite(ageSecondsIn) || ageSecondsIn < 0) {
      return '-';
    }
    if (ageSecondsIn < 60) {
      return ageSecondsIn.toString() + 's';
    }
    const mm = Math.floor(ageSecondsIn / 60);
    const ss = ageSecondsIn % 60;
    return mm.toString() + 'm ' + (ss < 10 ? ('0' + ss.toString()) : ss.toString()) + 's';
  }

  private refreshAutoRunLiveState() {
    const continueFlag = localStorage.getItem('vs_auto_batch_continue') === '1';
    const queueRaw = localStorage.getItem('vs_auto_batch_queue');
    const queue = queueRaw ? JSON.parse(queueRaw) : [];
    const total = Array.isArray(queue) ? queue.length : 0;

    if (!continueFlag || total < 1) {
      this.autoRunLiveVisible = false;
      this.autoRunLiveCurrent = 0;
      this.autoRunLiveTotal = 0;
      this.autoRunLivePercent = 0;
      this.autoRunLiveCurrentMaterial = '';
      this.autoRunLiveElapsedText = '00:00';
      this.autoRunLiveHeartbeatText = '-';
      this.autoRunLiveStatusText = '';
      this.autoRunLiveStalled = false;
      return;
    }

    const indexRaw = localStorage.getItem('vs_auto_batch_index');
    let completed = indexRaw ? Number(indexRaw) : 0;
    if (!Number.isFinite(completed) || completed < 0) {
      completed = 0;
    }
    if (completed > total) {
      completed = total;
    }

    const currentJobRaw = localStorage.getItem('vs_auto_batch_current_job');
    const currentJob = currentJobRaw ? JSON.parse(currentJobRaw) : null;
    const expectedJob = completed < total ? queue[completed] : null;
    const job = currentJob ? currentJob : expectedJob;
    const materialId = job && job.materialid !== undefined && job.materialid !== null ? job.materialid.toString() : '-';
    const materialDesc = job && job.materialdesc !== undefined && job.materialdesc !== null ? job.materialdesc.toString() : '';

    const jobStartRaw = localStorage.getItem('vs_auto_batch_job_started_at');
    const jobStartTs = jobStartRaw ? Number(jobStartRaw) : 0;
    const elapsedSec = jobStartTs > 0 ? Math.max(0, Math.floor((Date.now() - jobStartTs) / 1000)) : 0;

    let localAgeSec = -1;
    const localHbRaw = localStorage.getItem('vs_auto_batch_local_hb');
    if (localHbRaw) {
      try {
        const localHb = JSON.parse(localHbRaw);
        if (localHb && localHb.ts) {
          localAgeSec = Math.max(0, Math.floor((Date.now() - Number(localHb.ts)) / 1000));
        }
      } catch (e) {}
    }

    let backendAgeSec = -1;
    const backendHbRaw = localStorage.getItem('vs_auto_batch_backend_hb');
    if (backendHbRaw) {
      try {
        const backendHb = JSON.parse(backendHbRaw);
        if (backendHb && backendHb.ts) {
          backendAgeSec = Math.max(0, Math.floor((Date.now() - Number(backendHb.ts)) / 1000));
        }
      } catch (e) {}
    }

    const maxAge = Math.max(localAgeSec, backendAgeSec);
    const stalled = maxAge >= 20;

    this.autoRunLiveVisible = true;
    this.autoRunLiveCurrent = completed;
    this.autoRunLiveTotal = total;
    this.autoRunLivePercent = total > 0 ? Math.min(Math.round((completed * 100) / total), 100) : 0;
    this.autoRunLiveCurrentMaterial = materialId + (materialDesc ? (' - ' + materialDesc) : '');
    this.autoRunLiveElapsedText = this.formatSecondsToMMSS(elapsedSec);
    const candidateAges = [localAgeSec, backendAgeSec].filter(item => Number.isFinite(item) && item >= 0);
    const freshestAge = candidateAges.length > 0 ? Math.min(...candidateAges) : -1;
    this.autoRunLiveHeartbeatText = this.formatAgeText(freshestAge);
    this.autoRunLiveStalled = stalled;
    this.autoRunLiveStatusText = stalled
      ? 'Procesando combinaciones complejas...'
      : 'Proceso activo en segundo plano';
  }

  getAutoRunPulseDots(): string {
    return '.'.repeat((this.autoRunLivePulse % 4) + 1);
  }

  private buildMaterialRows(response: any): any[] {
    const listOut = [];
    const mapByMaterial = {};
    const piecesByMaterial = {};

    if (response && response.data_detail && response.data_detail.length) {
      for (let i = 0; i < response.data_detail.length; i++) {
        this.addMaterialToMap(mapByMaterial, response.data_detail[i], piecesByMaterial);
      }
    } else if (response && response.data && response.data.length) {
      for (let i = 0; i < response.data.length; i++) {
        this.addMaterialToMap(mapByMaterial, response.data[i], piecesByMaterial);
      }
    }

    const keys = Object.keys(mapByMaterial);
    for (let i = 0; i < keys.length; i++) {
      const row = mapByMaterial[keys[i]];
      const materialStats = piecesByMaterial[keys[i]];
      row.pieces_count = materialStats ? materialStats.pieces_count : 0;
      row.units_total = materialStats ? materialStats.units_total : 0;
      row.area_total_m2 = materialStats ? materialStats.area_total_m2 : 0;
      listOut.push(row);
    }

    this.floorPiecesByMaterial = piecesByMaterial;

    return listOut;
  }

  private addMaterialToMap(mapByMaterial: any, item: any, piecesByMaterial: any) {
    if (!item) {
      return;
    }

    const materialId = item.materialid;
    const materialDesc = item.materialdesc;

    if (materialId === null || materialId === undefined) {
      return;
    }

    if (!mapByMaterial[materialId]) {
      mapByMaterial[materialId] = {
        materialid: materialId,
        materialdesc: materialDesc ? materialDesc : '',
        remnant_width: 0,
        remnant_height: 0,
        remnant_units: 0,
        sheet_width: 6000,
        sheet_height: 3210,
        sheet_units: -1,
        min_tronz: 0,
        origin: 'LT',
        origin_border_x: 0,
        origin_border_y: 0,
        freedom: 'X',
        strategy: 'less_waste',
        export_mode: 'V40',
        speed: 'fast',
        cutting_table_id: null,
        max_trav: 2500,
        level: 20
      };
    }

    if (!piecesByMaterial[materialId]) {
      piecesByMaterial[materialId] = {
        pieces_count: 0,
        units_total: 0,
        area_total_m2: 0
      };
    }

    const width = this.toNumber(item.width_add ? item.width_add : item.width);
    const height = this.toNumber(item.height_add ? item.height_add : item.height);
    const units = this.toNumber(item.unitstotal ? item.unitstotal : item.units);
    const area = (width * height * units) / 1000000;

    piecesByMaterial[materialId].pieces_count += 1;
    piecesByMaterial[materialId].units_total += units;
    piecesByMaterial[materialId].area_total_m2 += Number(area.toFixed(4));
  }

  private buildFloorPickerRows(responseIn: any): any[] {
    const source = [];
    if (responseIn && responseIn.data_detail && responseIn.data_detail.length > 0) {
      for (let i = 0; i < responseIn.data_detail.length; i++) {
        source.push(responseIn.data_detail[i]);
      }
    } else if (responseIn && responseIn.data && responseIn.data.length > 0) {
      for (let i = 0; i < responseIn.data.length; i++) {
        source.push(responseIn.data[i]);
      }
    }

    const mapRows = {};
    for (let i = 0; i < source.length; i++) {
      const item = source[i];
      if (!item || item.batchno === null || item.batchno === undefined || item.materialid === null || item.materialid === undefined) {
        continue;
      }

      const key = item.batchno.toString() + '|' + item.materialid.toString();
      if (!mapRows[key]) {
        mapRows[key] = {
          batchno: item.batchno,
          materialid: item.materialid,
          materialdesc: item.materialdesc ? item.materialdesc : '',
          pieces_count: 0,
          units_total: 0,
          area_total_m2: 0
        };
      }

      const width = this.toNumber(item.width_add ? item.width_add : item.width);
      const height = this.toNumber(item.height_add ? item.height_add : item.height);
      const units = this.toNumber(item.unitstotal ? item.unitstotal : item.units);
      const area = (width * height * units) / 1000000;

      mapRows[key].pieces_count += 1;
      mapRows[key].units_total += units;
      mapRows[key].area_total_m2 += Number(area.toFixed(4));
    }

    const listOut = [];
    const keys = Object.keys(mapRows);
    for (let i = 0; i < keys.length; i++) {
      listOut.push(mapRows[keys[i]]);
    }
    listOut.sort((a, b) => {
      if (a.batchno === b.batchno) {
        return a.materialdesc.toString().localeCompare(b.materialdesc.toString());
      }
      return b.batchno.toString().localeCompare(a.batchno.toString());
    });
    return listOut;
  }

  private normalizeMaterialRow(rowIn: any): any {
    rowIn.remnant_width = this.toNumber(rowIn.remnant_width);
    rowIn.remnant_height = this.toNumber(rowIn.remnant_height);
    rowIn.remnant_units = this.toNumber(rowIn.remnant_units);
    rowIn.sheet_width = this.toNumber(rowIn.sheet_width);
    rowIn.sheet_height = this.toNumber(rowIn.sheet_height);
    rowIn.sheet_units = this.toNumber(rowIn.sheet_units);
    rowIn.min_tronz = this.toNumber(rowIn.min_tronz);
    rowIn.origin_border_x = this.toNumber(rowIn.origin_border_x);
    rowIn.origin_border_y = this.toNumber(rowIn.origin_border_y);
    rowIn.level = this.toNumber(rowIn.level);

    if (rowIn.sheet_width <= 0) {
      rowIn.sheet_width = 6000;
    }
    if (rowIn.sheet_height <= 0) {
      rowIn.sheet_height = 3210;
    }
    if (rowIn.sheet_units === 0) {
      rowIn.sheet_units = -1;
    }

    if (rowIn.remnant_width < 0) {
      rowIn.remnant_width = 0;
    }
    if (rowIn.remnant_height < 0) {
      rowIn.remnant_height = 0;
    }
    if (rowIn.remnant_units < 0) {
      rowIn.remnant_units = 0;
    }
    if (rowIn.origin_border_x < 0) {
      rowIn.origin_border_x = 0;
    }
    if (rowIn.origin_border_y < 0) {
      rowIn.origin_border_y = 0;
    }
    if (rowIn.min_tronz < 0) {
      rowIn.min_tronz = 0;
    }

    const allowedExportModes = ['V40', 'LSW', 'LIOPT', 'OTD', 'BTR', 'ELO', 'TEC'];
    if (!rowIn.export_mode || allowedExportModes.indexOf(rowIn.export_mode) === -1) {
      rowIn.export_mode = 'V40';
    }

    const allowedFreedoms = ['H', 'X', 'Y', 'Z'];
    if (!rowIn.freedom || allowedFreedoms.indexOf(rowIn.freedom) === -1) {
      rowIn.freedom = 'X';
    }

    const allowedSpeeds = ['fast', 'medium', 'optimal'];
    if (!rowIn.speed || allowedSpeeds.indexOf(rowIn.speed) === -1) {
      rowIn.speed = 'fast';
    }

    rowIn.cutting_table_id = this.toNumber(rowIn.cutting_table_id);
    if (rowIn.cutting_table_id <= 0) {
      rowIn.cutting_table_id = this.findDefaultCuttingTableId(rowIn.export_mode, rowIn);
    }
    const selectedTable = this.getCuttingTableById(rowIn.cutting_table_id);
    const cncFamily = this.getCncFamilyFromCuttingTable(selectedTable);
    if (cncFamily) {
      rowIn.export_mode = cncFamily;
    }

    rowIn.max_trav = this.toNumber(rowIn.max_trav);
    if (rowIn.max_trav <= 0) {
      this.refreshRowMaxTravFromCuttingTable(rowIn, true);
    }
    if (rowIn.max_trav <= 0) {
      rowIn.max_trav = 2500;
    }

    if (rowIn.level <= 0) {
      this.refreshRowLevelFromCuttingTable(rowIn, true);
    }
    if (rowIn.level <= 0) {
      rowIn.level = 20;
    }

    return rowIn;
  }

  onMaterialFieldChange(rowIndexIn: number, fieldIn: string, valueIn: any, numericIn: boolean = false) {
    if (!this.materialRows || !this.materialRows[rowIndexIn]) {
      return;
    }

    let valueAux: any = valueIn;
    if (numericIn) {
      valueAux = this.toNumber(valueIn);
    }

    this.materialRows[rowIndexIn][fieldIn] = valueAux;

    if (rowIndexIn === 0 && this.materialRows.length > 1) {
      const label = this.getMaterialFieldLabel(fieldIn);
      const answer = window.confirm('¿Aplicar el valor de "' + label + '" al resto de materiales?');
      if (answer) {
        for (let i = 1; i < this.materialRows.length; i++) {
          this.materialRows[i][fieldIn] = valueAux;
        }
      }
    }
  }

  onSheetUnitsModeChange(rowIndexIn: number, modeIn: string) {
    if (!this.materialRows || !this.materialRows[rowIndexIn]) {
      return;
    }

    if (modeIn === 'INF') {
      this.onMaterialFieldChange(rowIndexIn, 'sheet_units', -1, true);
      return;
    }

    if (this.materialRows[rowIndexIn].sheet_units === -1) {
      this.onMaterialFieldChange(rowIndexIn, 'sheet_units', 1, true);
    }
  }

  onSheetUnitsValueChange(rowIndexIn: number, valueIn: any) {
    this.onMaterialFieldChange(rowIndexIn, 'sheet_units', valueIn, true);
  }

  onMaterialCuttingTableChange(rowIndexIn: number, valueIn: any) {
    if (!this.materialRows || !this.materialRows[rowIndexIn]) {
      return;
    }

    const idSelected = this.toNumber(valueIn);
    this.materialRows[rowIndexIn].cutting_table_id = idSelected > 0 ? idSelected : null;

    const selectedTable = this.getCuttingTableById(this.materialRows[rowIndexIn].cutting_table_id);
    if (!selectedTable) {
      return;
    }

    const cncFamily = this.getCncFamilyFromCuttingTable(selectedTable);
    if (cncFamily) {
      this.materialRows[rowIndexIn].export_mode = cncFamily;
    }
    this.applyRowOriginBordersFromCuttingTable(this.materialRows[rowIndexIn], selectedTable, true);
    this.refreshRowSheetFromCuttingTable(this.materialRows[rowIndexIn], true);
    this.refreshRowMaxTravFromCuttingTable(this.materialRows[rowIndexIn], true);
    this.refreshRowLevelFromCuttingTable(this.materialRows[rowIndexIn], true);

    if (rowIndexIn === 0 && this.materialRows.length > 1) {
      const answer = window.confirm('¿Aplicar la misma mesa de corte al resto de materiales?');
      if (answer) {
        for (let i = 1; i < this.materialRows.length; i++) {
          this.materialRows[i].cutting_table_id = this.materialRows[rowIndexIn].cutting_table_id;
          if (cncFamily) {
            this.materialRows[i].export_mode = cncFamily;
          }
          this.applyRowOriginBordersFromCuttingTable(this.materialRows[i], selectedTable, true);
          this.refreshRowSheetFromCuttingTable(this.materialRows[i], true);
          this.refreshRowMaxTravFromCuttingTable(this.materialRows[i], true);
          this.refreshRowLevelFromCuttingTable(this.materialRows[i], true);
        }
      }
    }
  }

  private getMaterialFieldLabel(fieldIn: string): string {
    if (fieldIn === 'remnant_width') return 'Retal Ancho';
    if (fieldIn === 'remnant_height') return 'Retal Alto';
    if (fieldIn === 'remnant_units') return 'Retal Unid.';
    if (fieldIn === 'sheet_width') return 'Plancha Ancho';
    if (fieldIn === 'sheet_height') return 'Plancha Alto';
    if (fieldIn === 'sheet_units') return 'Plancha Unid.';
    if (fieldIn === 'origin') return 'Origen';
    if (fieldIn === 'origin_border_x') return 'Borde X (mm)';
    if (fieldIn === 'origin_border_y') return 'Borde Y (mm)';
    if (fieldIn === 'min_tronz') return 'Min.Tronz';
    if (fieldIn === 'max_trav') return 'Max.Trav';
    if (fieldIn === 'freedom') return 'Libertad';
    if (fieldIn === 'strategy') return 'Estrategia';
    if (fieldIn === 'export_mode') return 'Modo optimización';
    if (fieldIn === 'speed') return 'Velocidad';
    return fieldIn;
  }

  private hasUsableRemnant(rowIn: any): boolean {
    return rowIn.remnant_width > 0 && rowIn.remnant_height > 0 && rowIn.remnant_units > 0;
  }

  private toNumber(valueIn: any): number {
    const value = Number(valueIn);
    if (isNaN(value)) {
      return 0;
    }
    return value;
  }

  private extractCuttingTableInfo(stateIn: any): any {
    const out = { id: null, name: '' };
    if (stateIn === undefined || stateIn === null) {
      return out;
    }
    const txt = stateIn.toString();
    if (txt.trim() === '') {
      return out;
    }
    txt.split(';').forEach((item) => {
      const token = item ? item.toString().trim() : '';
      if (token.indexOf('CUTTING_TABLE_ID=') === 0) {
        const idTxt = token.substring('CUTTING_TABLE_ID='.length).trim();
        const idNum = Number(idTxt);
        out.id = Number.isFinite(idNum) && idNum > 0 ? idNum : null;
      }
      if (token.indexOf('CUTTING_TABLE_NAME=') === 0) {
        out.name = token.substring('CUTTING_TABLE_NAME='.length).trim();
      }
    });
    return out;
  }

  private resolveCuttingTableName(idIn: any, fallbackNameIn: any): string {
    const idNum = Number(idIn);
    if (Number.isFinite(idNum) && idNum > 0) {
      const table = this.getCuttingTableById(idNum);
      if (table && table.name) {
        return table.name.toString();
      }
    }
    return fallbackNameIn ? fallbackNameIn.toString() : '';
  }

  private calculateHistoricalAResto(itemIn: any): any {
    if (!itemIn) {
      return '-';
    }
    const gross = this.toNumber(itemIn.od_gross_area);
    const wastePct = this.toNumber(itemIn.od_porcentage_wastage);
    if (gross <= 0 || wastePct < 0) {
      return '-';
    }
    return Number(((gross * wastePct) / 100).toFixed(2));
  }

  private getHistoricalOptimizationTime(itemIn: any): any {
    if (!itemIn) {
      return { text: '-', seconds: 0 };
    }

    const storedSeconds = this.toNumber(itemIn.od_optimization_time);
    if (storedSeconds > 0) {
      return {
        text: this.formatSecondsAsDuration(storedSeconds),
        seconds: storedSeconds
      };
    }

    const startRaw = itemIn.od_cut_start_date ? itemIn.od_cut_start_date : null;
    const endRaw = itemIn.od_cut_end_date ? itemIn.od_cut_end_date : null;
    if (!startRaw || !endRaw) {
      return { text: '-', seconds: 0 };
    }

    const startDate = new Date(startRaw);
    const endDate = new Date(endRaw);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return { text: '-', seconds: 0 };
    }

    const diffMs = endDate.getTime() - startDate.getTime();
    const seconds = diffMs > 0 ? Number((diffMs / 1000).toFixed(2)) : 0;
    if (seconds <= 0) {
      return { text: '-', seconds: 0 };
    }

    return {
      text: this.formatSecondsAsDuration(seconds),
      seconds: seconds
    };
  }

  private calculateHistoricalACola(itemIn: any): any {
    if (!itemIn) {
      return '-';
    }
    const gross = this.toNumber(itemIn.od_gross_area);
    const net = this.toNumber(itemIn.od_net_area);
    const resto = this.calculateHistoricalAResto(itemIn);
    if (gross <= 0 || net < 0 || resto === '-') {
      return '-';
    }
    const cola = gross - net - Number(resto);
    // En histórico no guardamos explícitamente el área de cola.
    // Si el cálculo indirecto no devuelve valor positivo fiable, se muestra como no disponible.
    if (cola <= 0) {
      return '-';
    }
    return Number(cola.toFixed(2));
  }

  private getHistoricalACola(itemIn: any): any {
    if (!itemIn) {
      return '-';
    }

    // En BD usamos od_net_area_weight para guardar área de cola cuando está disponible.
    const colaStored = this.toNumber(itemIn.od_net_area_weight);
    if (colaStored > 0) {
      return Number(colaStored.toFixed(2));
    }

    return this.calculateHistoricalACola(itemIn);
  }

  private buildPlanPayload(rowIn: any, materialInfoIn: any): any {
    const lotList = this.parseLotNumbers(this.lotNumber);
    const selectedTable = this.getCuttingTableById(rowIn.cutting_table_id);
    const disbands = this.getDisbandValuesFromCuttingTable(selectedTable, rowIn);
    return {
      lot: lotList.join(','),
      lot_list: lotList,
      materialid: rowIn.materialid,
      materialdesc: rowIn.materialdesc,
      strategy: rowIn.strategy,
      freedom: rowIn.freedom,
      export_mode: rowIn.export_mode,
      cutting_table_id: rowIn.cutting_table_id ? Number(rowIn.cutting_table_id) : null,
      cutting_table_name: selectedTable && selectedTable.name ? selectedTable.name.toString() : '',
      level: rowIn.level,
      speed: rowIn.speed,
      max_trav: rowIn.max_trav,
      origin: rowIn.origin,
      origin_border_x: rowIn.origin_border_x,
      origin_border_y: rowIn.origin_border_y,
      disband_l: disbands.disband_l,
      disband_b: disbands.disband_b,
      disband_r: disbands.disband_r,
      disband_t: disbands.disband_t,
      min_tronz: rowIn.min_tronz,
      sheet: {
        width: rowIn.sheet_width,
        height: rowIn.sheet_height,
        units: rowIn.sheet_units
      },
      remnant: this.hasUsableRemnant(rowIn) ? {
        width: rowIn.remnant_width,
        height: rowIn.remnant_height,
        units: rowIn.remnant_units
      } : null,
      floor_summary: {
        pieces_count: materialInfoIn ? materialInfoIn.pieces_count : 0,
        units_total: materialInfoIn ? materialInfoIn.units_total : 0,
        area_total_m2: materialInfoIn ? materialInfoIn.area_total_m2 : 0
      }
    };
  }

  private getDisbandValuesFromCuttingTable(tableIn: any, rowIn: any = null): any {
    const parsed = this.parseCuttingTableParam4(tableIn ? tableIn.param4 : null);

    const getNum = (objIn: any, keysIn: string[], fallbackIn: number): number => {
      if (!objIn || typeof objIn !== 'object') {
        return fallbackIn;
      }
      const keyMap = {};
      Object.keys(objIn).forEach((k) => {
        keyMap[k.toLowerCase()] = objIn[k];
      });
      for (let i = 0; i < keysIn.length; i++) {
        const key = keysIn[i];
        const value = objIn[key] !== undefined ? objIn[key] : keyMap[key.toLowerCase()];
        if (value !== undefined && value !== null && value !== '') {
          const n = Number(value);
          if (!isNaN(n)) {
            return n;
          }
        }
      }
      return fallbackIn;
    };

    const disband_l = getNum(parsed, ['disband_l', 'f_disband_l'], this.toNumber(tableIn && tableIn.disband_l !== undefined ? tableIn.disband_l : 0));
    const disband_b = getNum(parsed, ['disband_b', 'f_disband_b'], this.toNumber(tableIn && tableIn.disband_b !== undefined ? tableIn.disband_b : 0));
    const disband_r = getNum(parsed, ['disband_r', 'f_disband_r'], this.toNumber(tableIn && tableIn.disband_r !== undefined ? tableIn.disband_r : 0));
    const disband_t = getNum(parsed, ['disband_t', 'f_disband_t'], this.toNumber(tableIn && tableIn.disband_t !== undefined ? tableIn.disband_t : 0));

    // Fallback si no hubiera mesa disponible: reconstruir desde origen + bordes X/Y del row.
    if (!tableIn && rowIn) {
      const origin = rowIn.origin ? rowIn.origin.toString() : 'LT';
      const borderX = this.toNumber(rowIn.origin_border_x);
      const borderY = this.toNumber(rowIn.origin_border_y);
      const out = { disband_l: 0, disband_b: 0, disband_r: 0, disband_t: 0 };
      if (origin === 'LB') {
        out.disband_b = borderX;
        out.disband_l = borderY;
      } else if (origin === 'RB') {
        out.disband_b = borderX;
        out.disband_r = borderY;
      } else if (origin === 'RT') {
        out.disband_t = borderX;
        out.disband_r = borderY;
      } else {
        out.disband_t = borderX;
        out.disband_l = borderY;
      }
      return out;
    }

    return {
      disband_l: disband_l,
      disband_b: disband_b,
      disband_r: disband_r,
      disband_t: disband_t
    };
  }

  private loadCuttingTables() {
    this.cuttingTableService.getCuttingTables(null).subscribe(
      (response: any[]) => {
        this.cuttingTables = response && response.length ? response.filter(item => item && item.active !== false) : [];
        this.applyDefaultCuttingTablesToRows();
        this.refreshResultRowsCuttingTableNames();
      },
      () => {
        this.cuttingTables = [];
      }
    );
  }

  private loadBendingMachines() {
    this.bendingMachineService.getBendingMachines({}).subscribe(
      (response: any[]) => {
        this.bendingMachines = response && response.length
          ? response.filter(item => this.isMachineActive(item))
          : [];
        if (this.bendingMachines.length > 0 && (!this.selectedBendingMachineId || this.selectedBendingMachineId < 1)) {
          this.selectedBendingMachineId = Number(this.bendingMachines[0].id);
        } else if (!this.bendingMachines || this.bendingMachines.length < 1) {
          this.infoMessage = 'No existe plegadora disponible para poder generar este fichero. Revise Maestros > Plegadoras.';
        }
      },
      () => {
        this.bendingMachines = [];
      }
    );
  }

  private isMachineActive(itemIn: any): boolean {
    if (!itemIn) {
      return false;
    }
    const activeRaw = itemIn.active;
    if (activeRaw === true) {
      return true;
    }
    if (activeRaw === false || activeRaw === null || activeRaw === undefined) {
      return false;
    }
    const num = Number(activeRaw);
    return Number.isFinite(num) ? num === 1 : (activeRaw.toString().toLowerCase() === 'true');
  }

  private applyDefaultCuttingTablesToRows() {
    if (!this.materialRows || this.materialRows.length < 1) {
      return;
    }

    for (let i = 0; i < this.materialRows.length; i++) {
      if (!this.materialRows[i]) {
        continue;
      }
      const currentId = this.toNumber(this.materialRows[i].cutting_table_id);
      if (currentId > 0) {
        const selectedTable = this.getCuttingTableById(currentId);
        const cncFamily = this.getCncFamilyFromCuttingTable(selectedTable);
        if (cncFamily) {
          this.materialRows[i].export_mode = cncFamily;
        }
        this.applyRowOriginBordersFromCuttingTable(this.materialRows[i], selectedTable, false);
        this.refreshRowSheetFromCuttingTable(this.materialRows[i], true);
        this.refreshRowMaxTravFromCuttingTable(this.materialRows[i], false);
        this.refreshRowLevelFromCuttingTable(this.materialRows[i], false);
        continue;
      }
      this.materialRows[i].cutting_table_id = this.findDefaultCuttingTableId(this.materialRows[i].export_mode, this.materialRows[i]);
      const selectedTable = this.getCuttingTableById(this.materialRows[i].cutting_table_id);
      const cncFamily = this.getCncFamilyFromCuttingTable(selectedTable);
      if (cncFamily) {
        this.materialRows[i].export_mode = cncFamily;
      }
      this.applyRowOriginBordersFromCuttingTable(this.materialRows[i], selectedTable, false);
      this.refreshRowSheetFromCuttingTable(this.materialRows[i], true);
      this.refreshRowMaxTravFromCuttingTable(this.materialRows[i], true);
      this.refreshRowLevelFromCuttingTable(this.materialRows[i], true);
    }
  }

  private applyRowOriginBordersFromCuttingTable(rowIn: any, tableIn: any, forceIn: boolean): void {
    if (!rowIn || !tableIn) {
      return;
    }

    const mapping = this.getOriginBorderMappingFromCuttingTable(tableIn);
    if (!mapping) {
      return;
    }

    const currentOrigin = rowIn.origin ? rowIn.origin.toString() : '';
    const currentBorderX = this.toNumber(rowIn.origin_border_x);
    const currentBorderY = this.toNumber(rowIn.origin_border_y);
    const canApply = forceIn || currentOrigin === '' || (currentBorderX === 0 && currentBorderY === 0);

    if (!canApply) {
      return;
    }

    rowIn.origin = mapping.origin;
    rowIn.origin_border_x = mapping.border_x;
    rowIn.origin_border_y = mapping.border_y;
  }

  private getOriginBorderMappingFromCuttingTable(tableIn: any): any {
    if (!tableIn) {
      return null;
    }

    const parsed = this.parseCuttingTableParam4(tableIn ? tableIn.param4 : null);
    const getNum = (objIn: any, keysIn: string[], fallbackIn: number): number => {
      if (!objIn || typeof objIn !== 'object') {
        return fallbackIn;
      }

      const keyMap = {};
      Object.keys(objIn).forEach((k) => {
        keyMap[k.toLowerCase()] = objIn[k];
      });

      for (let i = 0; i < keysIn.length; i++) {
        const key = keysIn[i];
        const value = objIn[key] !== undefined ? objIn[key] : keyMap[key.toLowerCase()];
        if (value !== undefined && value !== null && value !== '') {
          const n = Number(value);
          if (!isNaN(n)) {
            return n;
          }
        }
      }

      return fallbackIn;
    };

    const originNum = getNum(parsed, ['origin', 'f_origin'], this.toNumber(tableIn && tableIn.param1 ? tableIn.param1 : 1));
    const disbandL = getNum(parsed, ['disband_l', 'f_disband_l'], this.toNumber(tableIn && tableIn.disband_l !== undefined ? tableIn.disband_l : 0));
    const disbandB = getNum(parsed, ['disband_b', 'f_disband_b'], this.toNumber(tableIn && tableIn.disband_b !== undefined ? tableIn.disband_b : 0));
    const disbandR = getNum(parsed, ['disband_r', 'f_disband_r'], this.toNumber(tableIn && tableIn.disband_r !== undefined ? tableIn.disband_r : 0));
    const disbandT = getNum(parsed, ['disband_t', 'f_disband_t'], this.toNumber(tableIn && tableIn.disband_t !== undefined ? tableIn.disband_t : 0));

    switch (originNum) {
      case 2:
        return { origin: 'LB', border_x: disbandB, border_y: disbandL };
      case 3:
        return { origin: 'RB', border_x: disbandB, border_y: disbandR };
      case 4:
        return { origin: 'RT', border_x: disbandT, border_y: disbandR };
      case 1:
      default:
        return { origin: 'LT', border_x: disbandT, border_y: disbandL };
    }
  }

  private refreshResultRowsCuttingTableNames() {
    if (!this.resultRows || this.resultRows.length < 1) {
      return;
    }
    for (let i = 0; i < this.resultRows.length; i++) {
      const row = this.resultRows[i];
      if (!row) {
        continue;
      }
      row.cutting_table_name = this.resolveCuttingTableName(row.cutting_table_id, row.cutting_table_name);
    }
  }

  private findDefaultCuttingTableId(exportModeIn: string, rowIn: any = null): number {
    if (!this.cuttingTables || this.cuttingTables.length < 1) {
      return null;
    }

    const materialDesc = rowIn && rowIn.materialdesc ? rowIn.materialdesc.toString().toUpperCase() : '';
    const hasLamPattern =
      materialDesc.indexOf('LAM') !== -1 ||
      materialDesc.indexOf('STADIP') !== -1 ||
      materialDesc.indexOf('+') !== -1;
    const matchToken = hasLamPattern ? 'LAM' : 'MONO';
    const tokenCandidates = this.cuttingTables.filter((table) => {
      if (!table) return false;
      const txt = ((table.name ? table.name.toString() : '') + ' ' + (table.desc ? table.desc.toString() : '')).toUpperCase();
      return txt.indexOf(matchToken) !== -1;
    });

    if (tokenCandidates.length > 0) {
      if (tokenCandidates.length === 1) {
        return Number(tokenCandidates[0].id);
      }

      const areaM2 = Number(rowIn && rowIn.area_total_m2 !== undefined ? rowIn.area_total_m2 : 0);
      const pickToken = Number.isFinite(areaM2) && areaM2 >= 15 ? 'XL' : 'XS';
      const preferred = tokenCandidates.find((table) => {
        const txt = ((table.name ? table.name.toString() : '') + ' ' + (table.desc ? table.desc.toString() : '')).toUpperCase();
        return txt.indexOf(pickToken) !== -1;
      });
      if (preferred) {
        return Number(preferred.id);
      }

      return Number(tokenCandidates[0].id);
    }

    const exportMode = exportModeIn ? exportModeIn.toString().toUpperCase() : '';
    if (exportMode) {
      for (let i = 0; i < this.cuttingTables.length; i++) {
        const table = this.cuttingTables[i];
        const cnc = this.getCncFamilyFromCuttingTable(table);
        if (cnc === exportMode) {
          return Number(table.id);
        }
      }
    }

    return Number(this.cuttingTables[0].id);
  }

  private getCuttingTableById(idIn: any): any {
    const idNum = this.toNumber(idIn);
    if (idNum <= 0 || !this.cuttingTables || this.cuttingTables.length < 1) {
      return null;
    }
    return this.cuttingTables.find(item => Number(item.id) === idNum);
  }

  private getMaxTravFromCuttingTable(tableIn: any): number {
    if (!tableIn) {
      return 0;
    }
    const parsed = this.parseCuttingTableParam4(tableIn ? tableIn.param4 : null);

    const getNum = (objIn: any, keysIn: string[]): number => {
      if (!objIn || typeof objIn !== 'object') {
        return 0;
      }
      for (let i = 0; i < keysIn.length; i++) {
        const key = keysIn[i];
        if (objIn[key] !== undefined && objIn[key] !== null && objIn[key] !== '') {
          const num = Number(objIn[key]);
          if (!isNaN(num) && num > 0) {
            return num;
          }
        }
      }
      return 0;
    };

    const fromParam4 = getNum(parsed, ['max_diecutting', 'maxtrav', 'max_trav', 'max_travera', 'f_max_diecutting']);
    if (fromParam4 > 0) {
      return fromParam4;
    }

    const fromParam2 = Number(tableIn.param2);
    if (!isNaN(fromParam2) && fromParam2 > 0) {
      return fromParam2;
    }

    return 0;
  }

  private getLevelFromCuttingTable(tableIn: any): number {
    if (!tableIn) {
      return 0;
    }
    const parsed = this.parseCuttingTableParam4(tableIn ? tableIn.param4 : null);

    const getNum = (objIn: any, keysIn: string[]): number => {
      if (!objIn || typeof objIn !== 'object') {
        return 0;
      }
      for (let i = 0; i < keysIn.length; i++) {
        const key = keysIn[i];
        if (objIn[key] !== undefined && objIn[key] !== null && objIn[key] !== '') {
          const num = Number(objIn[key]);
          if (!isNaN(num) && num > 0) {
            return num;
          }
        }
      }
      return 0;
    };

    const fromParam4 = getNum(parsed, ['level', 'f_level']);
    if (fromParam4 > 0) {
      return fromParam4;
    }

    const fromParam3 = Number(tableIn.param3);
    if (!isNaN(fromParam3) && fromParam3 > 0) {
      return fromParam3;
    }

    return 0;
  }

  private getSheetSizeFromCuttingTable(tableIn: any): any {
    const out = { width: 0, height: 0 };
    if (!tableIn) {
      return out;
    }
    const parsed = this.parseCuttingTableParam4(tableIn ? tableIn.param4 : null);

    const getNum = (objIn: any, keysIn: string[]): number => {
      if (!objIn || typeof objIn !== 'object') {
        return 0;
      }
      for (let i = 0; i < keysIn.length; i++) {
        const key = keysIn[i];
        if (objIn[key] !== undefined && objIn[key] !== null && objIn[key] !== '') {
          const num = Number(objIn[key]);
          if (!isNaN(num) && num > 0) {
            return num;
          }
        }
      }
      return 0;
    };

    out.width = getNum(parsed, ['sheet_width', 'width', 'ancho', 'f_width']);
    out.height = getNum(parsed, ['sheet_height', 'height', 'alto', 'f_height']);

    if (out.width <= 0 || out.height <= 0) {
      const descTxt = tableIn && tableIn.desc ? tableIn.desc.toString() : '';
      const match = descTxt.match(/(\d{3,5})\s*[xX]\s*(\d{3,5})/);
      if (match && match.length > 2) {
        const w = Number(match[1]);
        const h = Number(match[2]);
        if (!isNaN(w) && w > 0) {
          out.width = w;
        }
        if (!isNaN(h) && h > 0) {
          out.height = h;
        }
      }
    }

    return out;
  }

  private refreshRowSheetFromCuttingTable(rowIn: any, forceIn: boolean) {
    if (!rowIn) {
      return;
    }
    if (!forceIn && rowIn.sheet_width && rowIn.sheet_height && Number(rowIn.sheet_width) > 0 && Number(rowIn.sheet_height) > 0) {
      return;
    }
    const selectedTable = this.getCuttingTableById(rowIn.cutting_table_id);
    const size = this.getSheetSizeFromCuttingTable(selectedTable);
    if (size.width > 0) {
      rowIn.sheet_width = size.width;
    }
    if (size.height > 0) {
      rowIn.sheet_height = size.height;
    }
  }

  private refreshRowMaxTravFromCuttingTable(rowIn: any, forceIn: boolean) {
    if (!rowIn) {
      return;
    }
    if (!forceIn && rowIn.max_trav && Number(rowIn.max_trav) > 0) {
      return;
    }
    const selectedTable = this.getCuttingTableById(rowIn.cutting_table_id);
    const maxTrav = this.getMaxTravFromCuttingTable(selectedTable);
    if (maxTrav > 0) {
      rowIn.max_trav = maxTrav;
    }
  }

  private refreshRowLevelFromCuttingTable(rowIn: any, forceIn: boolean) {
    if (!rowIn) {
      return;
    }
    if (!forceIn && rowIn.level && Number(rowIn.level) > 0) {
      return;
    }
    const selectedTable = this.getCuttingTableById(rowIn.cutting_table_id);
    const level = this.getLevelFromCuttingTable(selectedTable);
    if (level > 0) {
      rowIn.level = level;
    }
  }

  private getCncFamilyFromCuttingTable(tableIn: any): string {
    if (!tableIn) {
      return '';
    }

    const candidates = ['V40', 'LSW', 'LIOPT', 'OTD', 'BTR', 'ELO', 'TEC'];

    const directFamily = tableIn && tableIn.cnc_family ? tableIn.cnc_family.toString().toUpperCase().trim() : '';
    if (candidates.indexOf(directFamily) >= 0) {
      return directFamily;
    }

    const directMasterFamily = tableIn && tableIn.masterData && tableIn.masterData.cnc_family
      ? tableIn.masterData.cnc_family.toString().toUpperCase().trim()
      : '';
    if (candidates.indexOf(directMasterFamily) >= 0) {
      return directMasterFamily;
    }

    const parsed = this.parseCuttingTableParam4(tableIn ? tableIn.param4 : null);

    const getTxt = (objIn: any, keysIn: string[]): string => {
      if (!objIn || typeof objIn !== 'object') {
        return '';
      }
      for (let i = 0; i < keysIn.length; i++) {
        const key = keysIn[i];
        if (objIn[key] !== undefined && objIn[key] !== null && objIn[key] !== '') {
          return objIn[key].toString().toUpperCase();
        }
      }
      return '';
    };

    const cncParsed = getTxt(parsed, ['cnc_family', 'cnc', 'export', 'export_type']);
    if (candidates.indexOf(cncParsed) >= 0) {
      return cncParsed;
    }

    const textSrc = ((tableIn.desc ? tableIn.desc.toString() : '') + ' ' + (tableIn.path ? tableIn.path.toString() : '')).toUpperCase();
    for (let i = 0; i < candidates.length; i++) {
      if (textSrc.indexOf(candidates[i]) !== -1) {
        return candidates[i];
      }
    }

    return 'V40';
  }

  private parseCuttingTableParam4(param4In: any): any {
    if (param4In === undefined || param4In === null) {
      return null;
    }
    if (typeof param4In === 'object') {
      return param4In;
    }

    const txt = String(param4In).trim();
    if (txt === '') {
      return null;
    }

    const parseJsonSafe = (txtIn: string) => {
      try { return JSON.parse(txtIn); } catch (error) { return null; }
    };

    let parsed = parseJsonSafe(txt);
    if (parsed && typeof parsed === 'string') {
      const nested = parseJsonSafe(parsed);
      if (nested) {
        parsed = nested;
      }
    }
    if (parsed && typeof parsed === 'object') {
      return parsed.masterData && typeof parsed.masterData === 'object' ? parsed.masterData : parsed;
    }

    let txtAux = txt;
    if ((txtAux.startsWith('"') && txtAux.endsWith('"')) || (txtAux.startsWith("'") && txtAux.endsWith("'"))) {
      txtAux = txtAux.substring(1, txtAux.length - 1);
      txtAux = txtAux.replace(/\\"/g, '"');
    }

    const separators = txtAux.indexOf(';') >= 0 ? ';' : (txtAux.indexOf('|') >= 0 ? '|' : ',');
    const tokens = txtAux.split(separators).map(t => t.trim()).filter(t => t !== '');
    const out = {};

    tokens.forEach(token => {
      const idx = token.indexOf('=');
      if (idx < 1) {
        return;
      }
      const key = token.substring(0, idx).trim().toLowerCase();
      const value = token.substring(idx + 1).trim();
      out[key] = value;
    });

    return Object.keys(out).length > 0 ? out : null;
  }

  private resumeRealBatchIfNeeded() {
    const continueFlag = localStorage.getItem('vs_auto_batch_continue');
    const resultRaw = localStorage.getItem('vs_auto_batch_last_result');
    const canAutoResume = this.consumeAutoResumeHint();

    if (resultRaw) {
      const result = JSON.parse(resultRaw);
      localStorage.removeItem('vs_auto_batch_last_result');
      this.mergeAutoResult(result);
    }

    if (continueFlag === '1') {
      if (!canAutoResume) {
        // Evitar relanzar automáticamente ejecuciones antiguas al entrar manualmente en la vista.
        this.runningRealBatch = false;
        this.executionProgress = '';
        localStorage.removeItem('vs_auto_batch_continue');
        localStorage.removeItem('vs_auto_batch_current_job');
        return;
      }

      const queueRaw = localStorage.getItem('vs_auto_batch_queue');
      const indexRaw = localStorage.getItem('vs_auto_batch_index');

      if (queueRaw && indexRaw) {
        const queue = JSON.parse(queueRaw);
        const index = Number(indexRaw);

        if (index < queue.length) {
          this.runningRealBatch = true;
          this.executionProgress = 'Lanzando material ' + (index + 1) + ' de ' + queue.length + '...';
          setTimeout(() => this.launchNextRealJob(), 150);
          return;
        }
      }

      this.runningRealBatch = false;
      this.executionProgress = '';
      this.infoMessage = this.buildAutoBatchFinishedMessage();
      localStorage.removeItem('vs_auto_batch_continue');
      localStorage.removeItem('vs_auto_batch_current_job');
      localStorage.removeItem('vs_auto_batch_started_at');
      localStorage.removeItem('vs_auto_batch_job_started_at');
      localStorage.removeItem('vs_auto_batch_local_hb');
      localStorage.removeItem('vs_auto_batch_backend_hb');
    }
  }

  private consumeAutoResumeHint(): boolean {
    const raw = localStorage.getItem('vs_auto_batch_resume_hint');
    if (!raw) {
      return false;
    }

    localStorage.removeItem('vs_auto_batch_resume_hint');

    try {
      const data = JSON.parse(raw);
      const ts = data && data.ts ? Number(data.ts) : 0;
      if (ts <= 0) {
        return false;
      }
      const ageMs = Date.now() - ts;
      return ageMs >= 0 && ageMs <= 2 * 60 * 1000;
    } catch (e) {
      return false;
    }
  }

  private launchNextRealJob() {
    const queueRaw = localStorage.getItem('vs_auto_batch_queue');
    const indexRaw = localStorage.getItem('vs_auto_batch_index');

    if (!queueRaw || !indexRaw) {
      this.runningRealBatch = false;
      this.executionProgress = '';
      return;
    }

    const queue = JSON.parse(queueRaw);
    const index = Number(indexRaw);

    if (index >= queue.length) {
      this.runningRealBatch = false;
      this.executionProgress = '';
      this.persistCompletedBatchHistory();
      localStorage.removeItem('vs_auto_batch_continue');
      localStorage.removeItem('vs_auto_batch_current_job');
      localStorage.removeItem('vs_auto_batch_started_at');
      localStorage.removeItem('vs_auto_batch_job_started_at');
      localStorage.removeItem('vs_auto_batch_local_hb');
      localStorage.removeItem('vs_auto_batch_backend_hb');
      this.infoMessage = this.buildAutoBatchFinishedMessage();
      return;
    }

    localStorage.setItem('vs_auto_batch_current_job', JSON.stringify(queue[index]));
    this.router.navigate(['/file-list/auto']);
  }

  private mergeAutoResult(resultIn: any) {
    const resultsRaw = localStorage.getItem('vs_auto_batch_results');
    let results = [];

    if (resultsRaw) {
      results = JSON.parse(resultsRaw);
    }

    results.push(resultIn);
    localStorage.setItem('vs_auto_batch_results', JSON.stringify(results));

    const queueRaw = localStorage.getItem('vs_auto_batch_queue');
    if (queueRaw) {
      const queue = JSON.parse(queueRaw);
      if (queue && queue.length > 0 && queue[0].lot) {
        this.lotNumber = queue[0].lot.toString();
      }
      this.resultRows = results.map(item => ({
        materialid: item.materialid,
        materialdesc: item.materialdesc,
        cutting_table_id: item.cutting_table_id ? Number(item.cutting_table_id) : null,
        cutting_table_name: item.cutting_table_name ? item.cutting_table_name : '',
        level: item.level && Number(item.level) > 0 ? Number(item.level) : '-',
        status: item.status,
        strategy: this.formatStrategyLabel(item.strategy),
        speed: this.getSpeedLabel(item.speed),
        opti_id: item.opti_id,
        sheets_used: item.sheets_used,
        stacks: item.stacks,
        waste: item.waste,
        a_cola: item.a_cola,
        a_resto: item.a_resto,
        optimization_time: item.optimization_time ? item.optimization_time : '-',
        optimization_time_seconds: item.optimization_time_seconds ? Number(item.optimization_time_seconds) : 0,
        pdf_groups_section: item.pdf_groups_section ? item.pdf_groups_section : '',
        pdf_cutting_section: item.pdf_cutting_section ? item.pdf_cutting_section : '',
        pdf_graphics_section: item.pdf_graphics_section ? item.pdf_graphics_section : '',
        warnings: item.warnings ? item.warnings : [],
        warning_count: item.warning_count ? Number(item.warning_count) : 0,
        lots_label: this.getLotsUsageLabel(item.lots_used, item.lot_scope),
        pieces_count: item.pieces_count,
        units_total: item.units_total,
        area_total_m2: item.area_total_m2
      }));
      this.resultPage = 1;
      this.prefetchAutoRowsForCurrentResults();

      const indexRaw = localStorage.getItem('vs_auto_batch_index');
      const index = indexRaw ? Number(indexRaw) : 0;
      this.executionProgress = 'Procesado material ' + index + ' de ' + queue.length + '.';
    }
  }

  private buildAutoBatchFinishedMessage(): string {
    if (!this.resultRows || this.resultRows.length === 0) {
      return 'Ejecución real finalizada.';
    }

    const warningRows = this.resultRows.filter(row =>
      row && (
        (row.warning_count && Number(row.warning_count) > 0) ||
        (row.status && row.status.toString().toUpperCase().indexOf('AVISO') !== -1)
      )
    );

    if (warningRows.length === 0) {
      return 'Ejecución real finalizada.';
    }

    const warningTotal = warningRows.reduce((acc, row) => {
      const count = row && row.warning_count !== undefined && row.warning_count !== null
        ? Number(row.warning_count)
        : 0;
      return acc + (isNaN(count) ? 0 : count);
    }, 0);

    return 'Ejecución real finalizada con avisos en ' + warningRows.length + ' material(es) (' + warningTotal + ' aviso(s)). Revise la columna Estado.';
  }

  private delay(msIn: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, msIn));
  }

  private parseLotNumbers(valueIn: string): string[] {
    if (!valueIn) {
      return [];
    }

    const list = valueIn
      .split(',')
      .map(item => item.trim())
      .filter(item => item !== '' && /^[0-9]+$/.test(item));

    return Array.from(new Set(list));
  }

  openPdf(sectionIn: string) {
    if (!sectionIn) {
      return;
    }
    const url = this.resolveGroupPdfUrl(sectionIn);
    window.open(url, '_blank');
  }

  async openRowPdf(rowIn: any, typeIn: string) {
    this.errorMessage = '';
    this.infoMessage = '';

    if (!rowIn) {
      return;
    }

    const type = typeIn ? typeIn.toString().toLowerCase() : '';
    const hasOptiId = rowIn.opti_id && rowIn.opti_id !== '-';

    if (type === 'groups') {
      if (rowIn.pdf_groups_section) {
        this.openPdf(rowIn.pdf_groups_section);
        return;
      }
      if (!hasOptiId) {
        this.errorMessage = 'No hay optimización válida para abrir Caballetes.';
        return;
      }
      this.infoMessage = 'Descargando de BD el PDF de Caballetes de la optimización ' + rowIn.opti_id + '...';
      await this.tryHydrateGroupsAndCuttingFromCache(rowIn);
      if (rowIn.pdf_groups_section) {
        this.openPdf(rowIn.pdf_groups_section);
        return;
      }
      this.errorMessage = 'No se pudo abrir el PDF de Caballetes.';
      return;
    }

    if (type === 'cutting') {
      if (rowIn.pdf_cutting_section) {
        this.openPdf(rowIn.pdf_cutting_section);
        return;
      }
      if (!hasOptiId) {
        this.errorMessage = 'No hay optimización válida para abrir Secuencia.';
        return;
      }
      this.infoMessage = 'Descargando de BD el PDF de Secuencia de la optimización ' + rowIn.opti_id + '...';
      await this.tryHydrateGroupsAndCuttingFromCache(rowIn, true);
      if (rowIn.pdf_cutting_section) {
        this.openPdf(rowIn.pdf_cutting_section);
        return;
      }
      this.errorMessage = 'No se pudo abrir el PDF de Secuencia.';
      return;
    }

    if (type === 'graphics') {
      if (rowIn.pdf_graphics_section) {
        this.openPdf(rowIn.pdf_graphics_section);
        return;
      }
      if (!hasOptiId) {
        this.errorMessage = 'No hay optimización válida para abrir Gráficos.';
        return;
      }
      // Para Gráficos usamos el flujo histórico (worker) porque el endpoint print_sheets
      // requiere más contexto que un simple opti_id y en algunos entornos devuelve 400.
      this.pendingRowPdfOpen = {
        optiId: Number(rowIn.opti_id),
        type: 'graphics'
      };
      this.openHistoricalOptiGraphics(rowIn);
      return;
    }
  }

  async downloadAllPdfs() {
    this.errorMessage = '';
    this.infoMessage = '';

    const existingState = this.getBulkPdfState();
    if (existingState && existingState.running) {
      this.infoMessage = 'Ya hay una descarga masiva en curso.';
      return;
    }

    const ids = [];
    const missingIds = [];
    for (let i = 0; i < this.resultRows.length; i++) {
      const row = this.resultRows[i];
      if (row && row.opti_id && row.opti_id !== '-') {
        const optiIdNum = Number(row.opti_id);
        ids.push(optiIdNum);
        if (!this.hasReadyAllPdfLinks(row)) {
          missingIds.push(optiIdNum);
        }
      }
    }

    if (ids.length === 0) {
      this.errorMessage = 'No hay optimizaciones válidas para procesar.';
      return;
    }

    if (missingIds.length === 0) {
      this.bulkProgressVisible = true;
      this.bulkProgressCurrent = 0;
      this.bulkProgressTotal = 1;
      this.bulkProgressLabel = 'Enlaces ya disponibles. Creando ZIP...';

      const downloadResult = await this.downloadPdfZipBundle();
      this.bulkProgressVisible = false;
      if (downloadResult.downloaded > 0 && downloadResult.zip_name) {
        this.infoMessage = 'Descargado ZIP ' + downloadResult.zip_name + ' con ' + downloadResult.downloaded + ' PDF(s).';
      } else {
        this.errorMessage = 'No se pudo descargar el ZIP de PDFs.';
      }
      return;
    }

    this.setBulkPdfState({
      running: true,
      index: 0,
      phase: 'groups',
      ids: missingIds,
      purpose: 'download_all'
    });

    this.bulkProgressVisible = true;
    this.bulkProgressCurrent = 0;
    this.bulkProgressTotal = missingIds.length * 2;
    this.bulkProgressLabel = 'Iniciando proceso...';

    this.infoMessage = 'Iniciando generación automática de Caballetes + Gráficos por material...';
    this.processBulkPdfStep();
  }

  downloadAllCncs() {
    this.errorMessage = '';
    this.infoMessage = '';

    const existingPdfState = this.getBulkPdfState();
    if (existingPdfState && existingPdfState.running) {
      // Si la tarea activa es sólo de "preparar enlaces", se puede cancelar para priorizar CNC.
      if (existingPdfState.purpose && existingPdfState.purpose.toString() === 'prepare_links') {
        this.clearBulkPdfState();
        this.stopBulkHeartbeat();
        this.bulkProgressVisible = false;
      } else {
        this.infoMessage = 'Hay una tarea masiva de PDFs en curso. Espere a que finalice.';
        return;
      }
    }

    if (this.bulkCncRunning) {
      this.infoMessage = 'Ya hay una exportación masiva de CNCs en curso.';
      return;
    }

    const ids: number[] = [];
    for (let i = 0; i < this.resultRows.length; i++) {
      const row = this.resultRows[i];
      if (!row || !row.opti_id || row.opti_id === '-') {
        continue;
      }
      const optiIdNum = Number(row.opti_id);
      if (!isNaN(optiIdNum) && optiIdNum > 0) {
        ids.push(optiIdNum);
      }
    }

    if (ids.length === 0) {
      this.errorMessage = 'No hay optimizaciones válidas para exportar CNC.';
      return;
    }

    this.bulkCncRunning = true;
    this.bulkCncIds = ids;
    this.bulkCncIndex = 0;
    this.bulkCncFilesDownloaded = 0;
    this.bulkCncCollectedFiles = [];
    this.bulkProgressVisible = true;
    this.bulkProgressCurrent = 0;
    this.bulkProgressTotal = ids.length;
    this.bulkProgressLabel = 'Preparando exportación CNC...';
    this.startBulkHeartbeat();
    this.touchBulkActivity();
    this.prefetchAutoRowsForCurrentResults();
    console.log('[AutoOpti][CNC] Inicio descarga masiva. Opti IDs:', ids);

    this.processBulkCncStep();
  }

  printSummaryPdf() {
    this.errorMessage = '';
    this.infoMessage = '';

    if (!this.resultRows || this.resultRows.length === 0) {
      this.errorMessage = 'No hay datos de resumen para imprimir.';
      return;
    }

    const pendingWindow = window.open('', '_blank');

    const rows = this.resultRows.map((row, index) => {
      const rowSeconds = Number(row && row.optimization_time_seconds ? row.optimization_time_seconds : 0);
      const rowTimeRaw = this.normalizeSummaryText(row && row.optimization_time ? row.optimization_time.toString() : '', '');
      const rowTime = rowTimeRaw !== '' ? rowTimeRaw : (rowSeconds > 0 ? this.formatSecondsAsDuration(rowSeconds) : '-');

      return {
      idx: index + 1,
      material: (row.materialid ? row.materialid.toString() : '-') + ' - ' + (row.materialdesc ? row.materialdesc.toString() : '-') +
        (row.cutting_table_name ? (' | Mesa: ' + row.cutting_table_name.toString()) : '') +
        (row.level && row.level !== '-' ? (' | Nivel: ' + row.level.toString()) : ''),
      lots: this.normalizeSummaryText(row.lots_label, '-'),
      strategy: this.normalizeSummaryText(row.strategy, '-'),
      speed: this.normalizeSummaryText(row.speed, '-'),
      opti_id: this.normalizeSummaryText(row.opti_id, '-'),
      pieces: row.pieces_count !== undefined && row.pieces_count !== null ? row.pieces_count.toString() : '-',
      sheets: row.sheets_used !== undefined && row.sheets_used !== null ? row.sheets_used.toString() : '-',
      stacks: row.stacks !== undefined && row.stacks !== null ? row.stacks.toString() : '-',
      waste: this.normalizeSummaryText(row.waste, '-'),
      a_cola: row.a_cola === '-' ? '-' : Number(row.a_cola).toFixed(2),
      a_resto: row.a_resto === '-' ? '-' : Number(row.a_resto).toFixed(2),
      time: rowTime,
      time_seconds: rowSeconds > 0 ? rowSeconds : 0,
      status: this.normalizeSummaryText(row.status, '-')
    };
    });

    const userName = this.extractLoggedUserName();
    const generatedAtDisplay = this.formatDateTimeHuman(new Date());

    const payload = {
      action: 'PrintAutoOptiSummary',
      back: 'auto-opti-lote',
      summary_info: {
        title: 'Resumen optimización automática',
        lots: this.lotNumber ? this.lotNumber.toString() : '',
        generated_at: new Date().toISOString(),
        generated_at_display: generatedAtDisplay,
        user_name: userName,
        total_time: this.getTotalOptimizationTime(),
        rows: rows
      }
    };

    this.fileService.printEasels(payload).subscribe(
      (response: any) => {
        if (response && response.section) {
          const pdfUrl = this.resolveGroupPdfUrl(response.section);
          if (pendingWindow) {
            pendingWindow.location.href = pdfUrl;
            this.infoMessage = 'Resumen PDF generado correctamente.';
          } else {
            this.openPdf(response.section);
            this.infoMessage = 'Resumen generado. Si no se abrió una pestaña, permite popups para este sitio.';
          }
          return;
        }
        if (pendingWindow) {
          pendingWindow.close();
        }
        this.errorMessage = 'No se ha podido generar el PDF de resumen.';
      },
      (error: any) => {
        if (pendingWindow) {
          pendingWindow.close();
        }
        const errorText = error ? error.toString() : '';
        this.errorMessage = 'Error al generar el PDF de resumen.' + (errorText !== '' ? (' ' + errorText) : '');
      }
    );
  }

  printCompactSummaryPdf() {
    this.errorMessage = '';
    this.infoMessage = '';

    if (!this.resultRows || this.resultRows.length === 0) {
      this.errorMessage = 'No hay datos de resumen para imprimir.';
      return;
    }

    const lotList = this.getCompactLotListFromCurrentContext();
    if (!lotList || lotList.length === 0) {
      this.errorMessage = this.compactSummaryMode === 'profiles'
        ? 'No hay lotes válidos para generar el listado de perfiles.'
        : (this.compactSummaryMode === 'bending_cnc'
          ? 'No hay lotes válidos para generar el CNC de plegadora.'
          : 'No hay lotes válidos para generar el listado de compactación.');
      return;
    }

    if (this.compactSummaryMode === 'profiles') {
      this.printProfilesSummaryPdf(lotList);
      return;
    }

    if (this.compactSummaryMode === 'bending_cnc') {
      this.printBendingMachineCnc(lotList);
      return;
    }

    if ((this.compactSummaryMode === 'classic' || this.compactSummaryMode === 'hybrid_family' || this.compactSummaryMode === 'hybrid_family_plus') &&
        (this.selectedCompactEaselId === null || this.selectedCompactEaselId === undefined)) {
      this.errorMessage = 'Debe seleccionar un caballete para generar la vista 2D de compactación.';
      return;
    }

    this.printingCompactSummary = true;
    const pendingWindow = window.open('', '_blank');

    this.optiService.compactOpti({
      opti_lot: lotList.join(','),
      exact_lot: true,
      compact_mode: this.compactSummaryMode
    }).subscribe(
      (compactResponse: any) => {
        if (!compactResponse || !compactResponse.order_opti || !compactResponse.total_virtual) {
          this.printingCompactSummary = false;
          if (pendingWindow) {
            pendingWindow.close();
          }
          this.errorMessage = 'No se han obtenido datos válidos para compactación.';
          return;
        }

        this.compactStacksTotal = Array.isArray(compactResponse.total_virtual)
          ? compactResponse.total_virtual.length
          : null;

        this.fileService.printEasels({
          compact_list_info: compactResponse,
          easel_id: this.selectedCompactEaselId,
          action: 'PrintCompactPile',
          back: 'auto-opti-lote'
        }).subscribe(
          (response: any) => {
            this.printingCompactSummary = false;
            if (response && response.section) {
              const pdfUrl = this.resolveGroupPdfUrl(response.section);
              if (pendingWindow) {
                pendingWindow.location.href = pdfUrl;
              } else {
                this.openPdf(response.section);
              }
              this.infoMessage = 'Listado de compactación generado correctamente.';
              return;
            }
            if (pendingWindow) {
              pendingWindow.close();
            }
            this.errorMessage = 'No se ha podido generar el listado de compactación.';
          },
          (error: any) => {
            this.printingCompactSummary = false;
            if (pendingWindow) {
              pendingWindow.close();
            }
            const errorText = error ? error.toString() : '';
            this.errorMessage = 'Error al generar el listado de compactación.' + (errorText !== '' ? (' ' + errorText) : '');
          }
        );
      },
      (error: any) => {
        this.printingCompactSummary = false;
        if (pendingWindow) {
          pendingWindow.close();
        }
        const errorText = error ? error.toString() : '';
        this.errorMessage = 'Error al preparar los datos de compactación.' + (errorText !== '' ? (' ' + errorText) : '');
      }
    );
  }

  private printProfilesSummaryPdf(lotListIn: string[]) {
    this.printingCompactSummary = true;
    const pendingWindow = window.open('', '_blank');

    const payload = {
      action: 'PrintAutoOptiProfiles',
      back: 'auto-opti-lote',
      profiles_info: {
        lot_list: lotListIn,
        lots: this.lotNumber ? this.lotNumber.toString() : lotListIn.join(','),
        generated_at: new Date().toISOString(),
        generated_at_display: this.formatDateTimeHuman(new Date()),
        user_name: this.extractLoggedUserName(),
        include_grouped_profiles: this.profileSummaryIncludeGrouped
      }
    };

    this.fileService.printEasels(payload).subscribe(
      (response: any) => {
        this.printingCompactSummary = false;
        if (response && response.section) {
          const pdfUrl = this.resolveGroupPdfUrl(response.section);
          if (pendingWindow) {
            pendingWindow.location.href = pdfUrl;
          } else {
            this.openPdf(response.section);
          }
          this.infoMessage = 'Listado de perfiles generado correctamente.';
          return;
        }

        if (pendingWindow) {
          pendingWindow.close();
        }
        this.errorMessage = 'No se ha podido generar el listado de perfiles.';
      },
      (error: any) => {
        this.printingCompactSummary = false;
        if (pendingWindow) {
          pendingWindow.close();
        }

        let errorText = '';
        if (error && error.error) {
          if (typeof error.error === 'string') {
            errorText = error.error;
          } else {
            errorText = error.error.error || error.error.message || '';
          }
        }
        if (!errorText) {
          errorText = error ? error.toString() : '';
        }
        this.errorMessage = errorText !== ''
          ? errorText
          : 'Error al generar el listado de perfiles.';
      }
    );
  }

  private loadEasels() {
    this.easelService.getEasels({ f_active: false }).subscribe(
      (response: Easel[]) => {
        this.easelList = this.getSortedEasels(response);
        if ((this.selectedCompactEaselId === null || this.selectedCompactEaselId === undefined) && this.easelList.length > 0) {
          this.selectedCompactEaselId = Number(this.easelList[0].id);
        }
      },
      () => {
        this.easelList = [];
      }
    );
  }

  private getSortedEasels(easelListIn: Easel[]): Easel[] {
    if (!easelListIn || easelListIn.length < 1) {
      return [];
    }

    return easelListIn.slice().sort((a, b) => {
      const pa = Number(a && a.priority !== undefined && a.priority !== null ? a.priority : 999);
      const pb = Number(b && b.priority !== undefined && b.priority !== null ? b.priority : 999);
      if (pa !== pb) {
        return pa - pb;
      }
      const ia = Number(a && a.id !== undefined && a.id !== null ? a.id : 0);
      const ib = Number(b && b.id !== undefined && b.id !== null ? b.id : 0);
      return ia - ib;
    });
  }

  private printBendingMachineCnc(lotListIn: string[]) {
    if (!this.bendingMachines || this.bendingMachines.length < 1) {
      this.errorMessage = 'No existe plegadora disponible para poder generar este fichero. Revise Maestros > Plegadoras.';
      return;
    }

    const machineId = Number(this.selectedBendingMachineId);
    if (!Number.isFinite(machineId) || machineId < 1) {
      this.errorMessage = 'Seleccione una plegadora para generar el CNC.';
      return;
    }

    const selectedMachine = this.bendingMachines.find(item => Number(item && item.id) === machineId);
    const stdDiscount = Number(selectedMachine && selectedMachine.std_profile_discount);
    if (!Number.isFinite(stdDiscount) || stdDiscount <= 0) {
      this.errorMessage = 'La plegadora seleccionada tiene "descuento perfil estandar" a cero. Revíselo en maestros.';
      return;
    }

    this.executeBendingCncGeneration(lotListIn, machineId, null, false);
  }

  private executeBendingCncGeneration(
    lotListIn: string[],
    machineIdIn: number,
    manualCodesIn: any[] | null,
    applyManualCodesIn: boolean
  ) {
    this.printingCompactSummary = true;
    const payload: any = {
      action: 'CreateBendingCNC',
      back: 'auto-opti-lote',
      bending_info: {
        lot_list: lotListIn,
        lots: this.lotNumber ? this.lotNumber.toString() : lotListIn.join(','),
        machine_id: machineIdIn
      }
    };
    if (manualCodesIn && manualCodesIn.length > 0) {
      payload.bending_info.manual_codes = manualCodesIn;
      payload.bending_info.apply_manual_codes = applyManualCodesIn === true;
    }

    this.fileService.printEasels(payload).subscribe(
      (response: any) => {
        this.printingCompactSummary = false;
        if (response && response.manual_required === true) {
          this.openBendingCodesModal(response, lotListIn, machineIdIn);
          return;
        }
        if (response && response.content && response.file_name) {
          this.closeBendingCodesModal();
          this.downloadTextFile(response.content, response.file_name);
          this.infoMessage = 'CNC de plegadora generado correctamente.';
          return;
        }
        this.errorMessage = 'No se ha podido generar el CNC de plegadora.';
      },
      (error: any) => {
        this.printingCompactSummary = false;
        const errPayload = error && error.error ? error.error : null;
        if (errPayload && errPayload.code && errPayload.code.toString() === 'BENDING_CODES_MISSING') {
          this.openBendingCodesModal(errPayload, lotListIn, machineIdIn);
          return;
        }
        let errorText = '';
        if (errPayload) {
          if (typeof errPayload === 'string') {
            errorText = errPayload;
          } else {
            errorText = errPayload.error || errPayload.message || '';
          }
        }
        if (!errorText) {
          errorText = error ? error.toString() : '';
        }
        this.errorMessage = errorText !== ''
          ? errorText
          : 'Error al generar el CNC de plegadora.';
      }
    );
  }

  private openBendingCodesModal(errPayloadIn: any, lotListIn: string[], machineIdIn: number) {
    const missing = errPayloadIn && Array.isArray(errPayloadIn.missing_profiles)
      ? errPayloadIn.missing_profiles
      : [];

    this.bendingCodesMissingRows = missing.map((item: any) => ({
      profile_key: item && item.profile_key ? item.profile_key.toString() : '',
      profile_label: item && item.profile_label ? item.profile_label.toString() : '-',
      slots: item && item.slots ? item.slots.toString() : '',
      code: ''
    }));
    this.bendingCodesLotList = (lotListIn || []).map(item => item.toString());
    this.bendingCodesMachineId = Number(machineIdIn);
    this.bendingCodesModal = this.bendingCodesMissingRows.length > 0;
    if (!this.bendingCodesModal) {
      this.errorMessage = 'No se ha podido generar el CNC de plegadora.';
    }
  }

  closeBendingCodesModal() {
    this.bendingCodesModal = false;
    this.bendingCodesMissingRows = [];
    this.bendingCodesLotList = [];
    this.bendingCodesMachineId = null;
  }

  confirmBendingCodesModal() {
    const regexCode = /^[0-9A-Za-z]+$/;
    const manualCodes = [];
    for (let i = 0; i < this.bendingCodesMissingRows.length; i++) {
      const row = this.bendingCodesMissingRows[i];
      const codeRaw = row && row.code !== undefined && row.code !== null ? row.code.toString().trim() : '';
      if (codeRaw === '' || !regexCode.test(codeRaw)) {
        this.errorMessage = 'Revise los códigos Lombarda. Solo se permiten letras y números.';
        return;
      }
      manualCodes.push({
        profile_key: row.profile_key,
        profile: row.profile_label,
        code: codeRaw.toUpperCase()
      });
    }

    if (!this.bendingCodesLotList || this.bendingCodesLotList.length < 1 || !this.bendingCodesMachineId) {
      this.errorMessage = 'No se pudo recuperar el contexto para generar el CNC de plegadora.';
      return;
    }

    this.errorMessage = '';
    const doRepair = window.confirm('Se van a guardar estos códigos en ORDER_PLANNED.info_add para futuras descargas. ¿Desea continuar?');
    this.executeBendingCncGeneration(this.bendingCodesLotList, this.bendingCodesMachineId, manualCodes, doRepair === true);
  }

  private downloadTextFile(contentIn: string, fileNameIn: string) {
    const blob = new Blob([contentIn], { type: 'application/octet-stream' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileNameIn ? fileNameIn.toString() : 'bending.job';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  }

  private getBulkPdfState(): any {
    const raw = localStorage.getItem(this.bulkPdfStateKey);
    if (!raw) {
      return null;
    }
    try {
      const state = JSON.parse(raw);
      if (state && state.running) {
        const updatedAt = state.updated_at ? Number(state.updated_at) : 0;
        if (updatedAt > 0 && (Date.now() - updatedAt) > (5 * 60 * 1000)) {
          localStorage.removeItem(this.bulkPdfStateKey);
          return null;
        }
      }
      return state;
    } catch (e) {
      localStorage.removeItem(this.bulkPdfStateKey);
      return null;
    }
  }

  private setBulkPdfState(stateIn: any) {
    const stateOut = stateIn ? { ...stateIn, updated_at: Date.now() } : stateIn;
    localStorage.setItem(this.bulkPdfStateKey, JSON.stringify(stateOut));
  }

  private clearBulkPdfState() {
    localStorage.removeItem(this.bulkPdfStateKey);
    this.stopBulkHeartbeat();
  }

  private touchBulkActivity() {
    this.bulkLastStepTs = Date.now();
    this.bulkStalled = false;
  }

  private startBulkHeartbeat() {
    if (this.bulkHeartbeatTimer) {
      return;
    }

    this.touchBulkActivity();

    this.bulkHeartbeatTimer = setInterval(() => {
      this.bulkPulseFrame = (this.bulkPulseFrame + 1) % 4;
      if (this.isTopWindow()) {
        this.consumeHistoricalPdfResult();
      }
      this.syncBulkProgressFromState();

      const state = this.getBulkPdfState();
      if ((!state || !state.running) && !this.bulkCncRunning) {
        this.stopBulkHeartbeat();
        return;
      }

      if (this.bulkLastStepTs > 0 && (Date.now() - this.bulkLastStepTs) > this.bulkStallMs) {
        this.bulkStalled = true;
        if (state && state.running) {
          this.infoMessage = 'Sin respuesta en una tarea PDF. Continuando con la siguiente...';
          this.forceAdvanceBulkState();
          return;
        }

        if (this.bulkCncRunning) {
          this.infoMessage = 'Sin respuesta en una tarea CNC. Continuando con la siguiente...';
          this.bulkCncIndex += 1;
          this.bulkProgressCurrent = this.bulkCncIndex;
          this.touchBulkActivity();
          setTimeout(() => this.processBulkCncStep(), 250);
        }
      }
    }, 1000);
  }

  private stopBulkHeartbeat() {
    if (this.bulkHeartbeatTimer) {
      clearInterval(this.bulkHeartbeatTimer);
      this.bulkHeartbeatTimer = null;
    }
    this.bulkLastStepTs = 0;
    this.bulkStalled = false;
  }

  private forceAdvanceBulkState() {
    const state = this.getBulkPdfState();
    if (!state || !state.running || !state.ids || state.ids.length === 0) {
      return;
    }

    if (state.phase === 'groups') {
      state.phase = 'graphics';
    } else {
      state.index += 1;
      state.phase = 'groups';
    }
    this.setBulkPdfState(state);
    this.bulkProgressCurrent = Math.min(this.bulkProgressCurrent + 1, this.bulkProgressTotal);
    this.touchBulkActivity();

    setTimeout(() => {
      this.processBulkPdfStep();
    }, 450);
  }

  getBulkPulseDots(): string {
    return '.'.repeat((this.bulkPulseFrame % 4) + 1);
  }

  private async processBulkPdfStep() {
    if (this.ignoreHistoricalPdfResults) {
      this.bulkProgressVisible = false;
      return;
    }
    const state = this.getBulkPdfState();
    if (!state || !state.running) {
      this.bulkProgressVisible = false;
      this.stopBulkHeartbeat();
      return;
    }
    this.startBulkHeartbeat();
    this.touchBulkActivity();

    if (!state.ids || state.index >= state.ids.length) {
      this.finishBulkPdfFlow();
      return;
    }

    const currentId = Number(state.ids[state.index]);
    const row = this.resultRows.find(item => Number(item.opti_id) === currentId);
    if (!row) {
      state.index += 1;
      state.phase = 'groups';
      this.setBulkPdfState(state);
      this.processBulkPdfStep();
      return;
    }

    this.infoMessage = 'Procesando ' + (state.index + 1) + ' de ' + state.ids.length + ': opti ' + currentId + ' (' + (state.phase === 'groups' ? 'Caballetes' : 'Gráficos') + ')...';
    this.bulkProgressLabel = 'Descargando de BD el PDF de ' + (state.phase === 'groups' ? 'Caballetes' : 'Gráficos') + ' para optimización ' + currentId + '...';

    if (state.phase === 'groups') {
      // En descarga masiva evitamos el intento directo a print_easels porque en
      // algunos entornos responde 500 sin contexto completo y ensucia la consola.
      if (row.pdf_groups_section) {
        this.advanceBulkStateForCurrentRow(currentId);
        return;
      }
      this.openHistoricalOptiForPdf(row);
      return;
    }

    // Para gráficos evitamos el intento directo a print_sheets (suele devolver 400
    // sin contexto completo) y usamos siempre el flujo histórico.
    this.openHistoricalOptiGraphics(row);
  }

  private async finishBulkPdfFlow() {
    if (this.ignoreHistoricalPdfResults) {
      this.clearBulkPdfState();
      this.bulkProgressVisible = false;
      return;
    }
    const state = this.getBulkPdfState();
    const purpose = state && state.purpose ? state.purpose.toString() : 'download_all';
    this.clearBulkPdfState();
    if (purpose === 'prepare_links') {
      this.bulkProgressVisible = false;
      this.infoMessage = 'Enlaces PDF preparados para el resumen cargado.';
      return;
    }

    this.infoMessage = 'Generación finalizada. Preparando descarga de PDFs...';
    this.bulkProgressLabel = 'Preparando ZIP de PDFs...';
    await this.delay(1800);

    const downloadResult = await this.downloadPdfZipBundle();
    const downloaded = downloadResult.downloaded;
    const totalPdfs = downloadResult.total;
    const zipName = downloadResult.zip_name;

    this.bulkProgressVisible = false;

    if (downloaded > 0 && zipName) {
      this.infoMessage = 'Proceso finalizado. ZIP ' + zipName + ' descargado (' + downloaded + ' PDF(s)).';
      return;
    }

    this.errorMessage = 'Proceso finalizado, pero no se pudo descargar el ZIP de PDFs.';
  }

  private sanitizePdfBundleToken(valueIn: any, fallbackIn: string = ''): string {
    const raw = valueIn !== undefined && valueIn !== null ? valueIn.toString().trim() : '';
    if (!raw) {
      return fallbackIn;
    }
    const normalized = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const safe = normalized.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    if (!safe) {
      return fallbackIn;
    }
    return safe;
  }

  private getPdfPrefixFromSection(sectionIn: string, typeIn: string): string {
    const rawFile = this.extractPdfFileName(sectionIn || '');
    const lower = rawFile.toLowerCase();
    if (lower.startsWith('graficos')) {
      return 'graficos';
    }
    if (lower.startsWith('modo2_grupos') || lower.startsWith('modo2_pilas') || lower.startsWith('pilas')) {
      return 'pilas';
    }
    if (lower.startsWith('modo2_tronzado') || lower.startsWith('tronzado')) {
      return 'tronzado';
    }
    if (typeIn === 'groups') {
      return 'pilas';
    }
    if (typeIn === 'cutting') {
      return 'tronzado';
    }
    if (typeIn === 'graphics') {
      return 'graficos';
    }
    return 'pdf';
  }

  private buildPdfBundleFileName(sectionIn: string, rowIn: any, typeIn: string): string {
    const prefix = this.getPdfPrefixFromSection(sectionIn, typeIn);
    const optiId = this.sanitizePdfBundleToken(rowIn && rowIn.opti_id ? rowIn.opti_id : '', 'sinopti');
    const matDescRaw = this.sanitizePdfBundleToken(rowIn && rowIn.materialdesc ? rowIn.materialdesc : '', 'material');
    const matDesc = matDescRaw.length > 48 ? matDescRaw.substring(0, 48) : matDescRaw;
    return `${prefix}_opti${optiId}_${matDesc}.pdf`;
  }

  private collectUniquePdfBundleEntries(): any[] {
    const entries = [];
    const seenSections = {};
    const usedNames = {};

    const pushEntry = (sectionIn: string, rowIn: any, typeIn: string) => {
      if (!sectionIn) {
        return;
      }
      const sectionKey = sectionIn.toString();
      if (seenSections[sectionKey]) {
        return;
      }
      seenSections[sectionKey] = true;

      let fileName = this.buildPdfBundleFileName(sectionKey, rowIn, typeIn);
      if (usedNames[fileName]) {
        const ext = '.pdf';
        const base = fileName.toLowerCase().endsWith(ext) ? fileName.substring(0, fileName.length - ext.length) : fileName;
        let n = usedNames[fileName];
        let candidate = '';
        do {
          n += 1;
          candidate = `${base}_${n}${ext}`;
        } while (usedNames[candidate]);
        usedNames[fileName] = n;
        fileName = candidate;
      }
      usedNames[fileName] = 1;

      entries.push({
        section: sectionKey,
        file_name: fileName
      });
    };

    for (let i = 0; i < this.resultRows.length; i++) {
      const row = this.resultRows[i];
      if (!row) {
        continue;
      }
      pushEntry(row.pdf_groups_section, row, 'groups');
      pushEntry(row.pdf_graphics_section, row, 'graphics');
    }

    return entries;
  }

  private async downloadPdfZipBundle(): Promise<any> {
    const entries = this.collectUniquePdfBundleEntries();
    const extraEntries = await this.collectExtraPdfBundleEntries();
    const allEntries = this.mergePdfBundleEntries(entries.concat(extraEntries));
    if (allEntries.length === 0) {
      return { downloaded: 0, total: 0, zip_name: '' };
    }

    this.bulkProgressLabel = 'Empaquetando PDFs en ZIP...';
    const zipName = this.buildBulkZipName('pdfs');
    const payload = {
      action: 'CreateZipBundle',
      back: 'auto-opti-lote',
      zip_name: zipName,
      files: allEntries
    };

    const response: any = await new Promise(resolve => {
      this.fileService.printEasels(payload).subscribe(
        (res: any) => resolve(res),
        () => resolve(null)
      );
    });

    if (!response || !response.section) {
      return { downloaded: 0, total: allEntries.length, zip_name: zipName };
    }

    this.bulkProgressLabel = 'Descargando ZIP de PDFs...';
    const ok = await this.downloadPdf(response.section);

    return {
      downloaded: ok ? allEntries.length : 0,
      total: allEntries.length,
      zip_name: zipName
    };
  }

  private mergePdfBundleEntries(entriesIn: any[]): any[] {
    const out = [];
    const used = {};
    const entries = Array.isArray(entriesIn) ? entriesIn : [];
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if (!entry || !entry.section || !entry.file_name) {
        continue;
      }
      const key = entry.section.toString() + '|' + entry.file_name.toString();
      if (used[key]) {
        continue;
      }
      used[key] = true;
      out.push({
        section: entry.section.toString(),
        file_name: entry.file_name.toString()
      });
    }
    return out;
  }

  private async collectExtraPdfBundleEntries(): Promise<any[]> {
    const entries = [];
    const lotList = this.parseLotNumbers(this.lotNumber);
    if (!lotList || lotList.length < 1) {
      return entries;
    }

    const lot = lotList[0].toString();
    const generatedAt = new Date();

    // Listado perfiles.
    const profilesSection = await this.requestPdfSection({
      action: 'PrintAutoOptiProfiles',
      back: 'auto-opti-lote',
      profiles_info: {
        lot_list: [lot],
        lots: lot,
        generated_at: generatedAt.toISOString(),
        generated_at_display: this.formatDateTimeHuman(generatedAt),
        user_name: this.extractLoggedUserName(),
        include_grouped_profiles: this.profileSummaryIncludeGrouped
      }
    });
    if (profilesSection) {
      entries.push({
        section: profilesSection,
        file_name: 'list-perf-' + lot + '.pdf'
      });
    }

    // Listado producción ampliado.
    const prodExtendedSection = await this.requestPdfSection({
      action: 'PrintDivideLot',
      back: 'auto-opti-lote',
      print_mode: 'extended',
      lot_info: {
        lot_0: lot,
        lot_1: lot,
        lot_2: null
      }
    });
    if (prodExtendedSection) {
      entries.push({
        section: prodExtendedSection,
        file_name: 'list-prod-xl-' + lot + '.pdf'
      });
    }

    return entries;
  }

  private async requestPdfSection(payloadIn: any): Promise<string> {
    if (!payloadIn) {
      return '';
    }
    const response: any = await new Promise(resolve => {
      this.fileService.printEasels(payloadIn).subscribe(
        (res: any) => resolve(res),
        () => resolve(null)
      );
    });
    if (response && response.section) {
      const section = response.section.toString();
      await this.waitForPdfSectionReady(section);
      return section;
    }
    return '';
  }

  private async waitForPdfSectionReady(sectionIn: string): Promise<void> {
    if (!sectionIn) {
      return;
    }

    const urls = this.getGroupPdfUrlCandidates(sectionIn);
    if (!urls || urls.length < 1) {
      return;
    }

    for (let attempt = 1; attempt <= 8; attempt++) {
      for (let i = 0; i < urls.length; i++) {
        try {
          const join = urls[i].indexOf('?') === -1 ? '?' : '&';
          const url = encodeURI(urls[i] + join + 'ts=' + Date.now().toString() + '_' + attempt.toString() + '_' + i.toString());
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response || !response.ok) {
            continue;
          }
          const blob = await response.blob();
          if (blob && blob.size > 1024) {
            return;
          }
        } catch (e) {
          // Siguiente intento/candidato.
        }
      }
      await this.delay(350);
    }
  }

  private async downloadPdf(sectionIn: string): Promise<boolean> {
    if (!sectionIn) {
      return false;
    }

    const urlCandidates = this.getGroupPdfUrlCandidates(sectionIn);
    const fileName = this.extractPdfFileName(sectionIn);

    for (let attempt = 1; attempt <= 8; attempt++) {
      for (let i = 0; i < urlCandidates.length; i++) {
        try {
          const cacheBuster = 'ts=' + Date.now().toString() + '_' + attempt.toString() + '_' + i.toString();
          const join = urlCandidates[i].indexOf('?') === -1 ? '?' : '&';
          const url = encodeURI(urlCandidates[i] + join + cacheBuster);
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response || !response.ok) {
            continue;
          }

          const blob = await response.blob();
          if (!blob || blob.size === 0) {
            continue;
          }

          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
          return true;
        } catch (e) {
          // siguiente candidato
        }
      }

      await this.delay(1300);
    }

    return false;
  }

  private resolveGroupPdfUrl(sectionIn: string): string {
    const candidates = this.getGroupPdfUrlCandidates(sectionIn);
    return candidates.length > 0 ? candidates[0] : (this.resolveBackendBaseUrl() + '/assets/attachments/documents/' + this.extractPdfFileName(sectionIn));
  }

  private getGroupPdfUrlCandidates(sectionIn: string): string[] {
    const section = sectionIn.toString().trim().replace(/\\/g, '/');
    const candidates = [];
    const normalized = section.replace(/^\.?\//, '');
    const backendBase = this.resolveBackendBaseUrl();
    const currentOrigin = window && window.location && window.location.origin ? window.location.origin : '';

    if (section.startsWith('http://') || section.startsWith('https://')) {
      candidates.push(section);
      try {
        const parsed = new URL(section);
        const pathWithQuery = (parsed.pathname ? parsed.pathname : '') + (parsed.search ? parsed.search : '');
        if (pathWithQuery) {
          if (backendBase) {
            candidates.push(backendBase + pathWithQuery);
          }
          if (currentOrigin) {
            candidates.push(currentOrigin + pathWithQuery);
          }
        }
      } catch (error) {
        // Si no se puede parsear, mantenemos el valor original.
      }
    }

    // Caso 1: ya viene como ruta web de assets.
    if (section.startsWith('/assets/')) {
      candidates.push(section);
    } else if (normalized.startsWith('assets/')) {
      candidates.push('/' + normalized);
    }

    // Caso 2: contiene un segmento assets/ intermedio dentro de una ruta absoluta de servidor.
    const assetsIdx = section.indexOf('/assets/');
    if (assetsIdx >= 0) {
      candidates.push(section.substring(assetsIdx));
    }
    const assetsIdxNoSlash = section.indexOf('assets/');
    if (assetsIdxNoSlash >= 0) {
      candidates.push('/' + section.substring(assetsIdxNoSlash));
    }

    // Caso 3: viene desde attachments/documents.
    const attachIdx = section.indexOf('attachments/documents/');
    if (attachIdx >= 0) {
      candidates.push('/assets/' + section.substring(attachIdx));
    } else if (normalized.startsWith('attachments/documents/')) {
      candidates.push('/assets/' + normalized);
    } else if (normalized.startsWith('documents/')) {
      candidates.push('/assets/attachments/' + normalized);
    }

    // Caso 4: fallback por nombre de archivo.
    const fileName = this.extractPdfFileName(section);
    candidates.push('/assets/attachments/documents/' + fileName);

    const unique = [];
    for (let i = 0; i < candidates.length; i++) {
      if (candidates[i] && !unique.includes(candidates[i])) {
        unique.push(candidates[i]);
      }
    }

    const prioritized = [];
    for (let i = 0; i < unique.length; i++) {
      const candidate = unique[i];
      if (!candidate) {
        continue;
      }

      if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
        if (!prioritized.includes(candidate)) {
          prioritized.push(candidate);
        }
        continue;
      }

      const absoluteBackend = candidate.startsWith('/')
        ? (backendBase + candidate)
        : (backendBase + '/' + candidate.replace(/^\/+/, ''));
      if (!prioritized.includes(absoluteBackend)) {
        prioritized.push(absoluteBackend);
      }

      const absoluteCurrent = currentOrigin
        ? (candidate.startsWith('/')
          ? (currentOrigin + candidate)
          : (currentOrigin + '/' + candidate.replace(/^\/+/, '')))
        : '';
      if (absoluteCurrent && !prioritized.includes(absoluteCurrent)) {
        prioritized.push(absoluteCurrent);
      }

      // Fallback relativo (por si el backend comparte origin en producción).
      if (!prioritized.includes(candidate)) {
        prioritized.push(candidate);
      }
    }
    return prioritized;
  }

  private resolveBackendBaseUrl(): string {
    const apiUrl = this.globals && this.globals.apiUrl ? String(this.globals.apiUrl) : '';
    if (apiUrl !== '') {
      return apiUrl.replace(/\/api\/?$/, '');
    }

    const host = window && window.location ? window.location.hostname : '';
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    if (isLocal && this.globals && this.globals.apiUrlList && this.globals.apiUrlList['local']) {
      return String(this.globals.apiUrlList['local']).replace(/\/api\/?$/, '');
    }

    return '';
  }

  private navigateHistoricalWorker(targetUrlIn: string) {
    if (!targetUrlIn) {
      return;
    }

    let frame = document.getElementById(this.historicalPdfWorkerFrameId) as HTMLIFrameElement;
    if (!frame) {
      frame = document.createElement('iframe');
      frame.id = this.historicalPdfWorkerFrameId;
      frame.style.display = 'none';
      document.body.appendChild(frame);
    }

    const sep = targetUrlIn.indexOf('?') >= 0 ? '&' : '?';
    frame.src = targetUrlIn + sep + 'job_ts=' + Date.now().toString();
  }

  private extractPdfFileName(sectionIn: string): string {
    const sectionParts = sectionIn.replace(/\\/g, '/').split('/');
    if (sectionParts && sectionParts.length > 0) {
      return sectionParts[sectionParts.length - 1];
    }
    return sectionIn;
  }

  openHistoricalOptiCnc(rowIn: any) {
    this.errorMessage = '';
    this.infoMessage = '';

    if (!rowIn || !rowIn.opti_id || rowIn.opti_id === '-') {
      this.errorMessage = 'No se puede cargar la optimización para exportar CNC.';
      return;
    }

    this.persistHistoricalSummaryState();
    localStorage.setItem('vs_auto_historical_pdf_job', JSON.stringify({
      opti_id: rowIn.opti_id,
      lot: this.lotNumber ? this.lotNumber.trim() : '',
      mode: 'cnc_only',
      created_at: Date.now()
    }));

    const urlTree = this.router.createUrlTree(
      ['/file-list', 'charge'],
      { queryParams: { opti_id: rowIn.opti_id, from: 'auto-opti-pdf-job' } }
    );
    const serialized = this.router.serializeUrl(urlTree);
    const targetUrl = '/#' + serialized;

    this.navigateHistoricalWorker(targetUrl);
    this.infoMessage = 'Descargando de BD el PDF de CNC de la optimización ' + rowIn.opti_id + '...';
  }

  openHistoricalOptiForPdf(rowIn: any) {
    this.errorMessage = '';
    this.infoMessage = '';

    if (!rowIn || !rowIn.opti_id || rowIn.opti_id === '-') {
      this.errorMessage = 'No se puede cargar la optimización histórica.';
      return;
    }

    this.persistHistoricalSummaryState();
    localStorage.setItem('vs_auto_historical_pdf_job', JSON.stringify({
      opti_id: rowIn.opti_id,
      lot: this.lotNumber ? this.lotNumber.trim() : '',
      mode: 'pdf_docs',
      created_at: Date.now()
    }));

    const urlTree = this.router.createUrlTree(
      ['/file-list', 'charge'],
      { queryParams: { opti_id: rowIn.opti_id, from: 'auto-opti-pdf-job' } }
    );
    const serialized = this.router.serializeUrl(urlTree);
    const targetUrl = '/#' + serialized;

    this.navigateHistoricalWorker(targetUrl);
    this.infoMessage = 'Descargando de BD el PDF de Caballetes y Secuencia de la optimización ' + rowIn.opti_id + '...';
  }

  openHistoricalOptiGraphics(rowIn: any) {
    this.errorMessage = '';
    this.infoMessage = '';

    if (!rowIn || !rowIn.opti_id || rowIn.opti_id === '-') {
      this.errorMessage = 'No se puede cargar la optimización para generar Gráficos.';
      return;
    }

    this.persistHistoricalSummaryState();
    localStorage.setItem('vs_auto_historical_pdf_job', JSON.stringify({
      opti_id: rowIn.opti_id,
      lot: this.lotNumber ? this.lotNumber.trim() : '',
      mode: 'graphics_only',
      created_at: Date.now()
    }));

    const urlTree = this.router.createUrlTree(
      ['/file-list', 'charge'],
      { queryParams: { opti_id: rowIn.opti_id, from: 'auto-opti-pdf-job' } }
    );
    const serialized = this.router.serializeUrl(urlTree);
    const targetUrl = '/#' + serialized;

    this.navigateHistoricalWorker(targetUrl);
    this.infoMessage = 'Descargando de BD el PDF de Gráficos de la optimización ' + rowIn.opti_id + '...';
  }

  openHistoricalOptiCuts(rowIn: any) {
    this.errorMessage = '';
    this.infoMessage = '';

    if (!rowIn || !rowIn.opti_id || rowIn.opti_id === '-') {
      this.errorMessage = 'No se puede abrir la optimización para ver cortes.';
      return;
    }

    const urlTree = this.router.createUrlTree(
      ['/file-list', 'charge'],
      { queryParams: { opti_id: rowIn.opti_id, from: 'auto-opti-cuts' } }
    );
    const serialized = this.router.serializeUrl(urlTree);
    const tabUrl = '/#' + serialized;
    window.open(tabUrl, '_blank');
    this.infoMessage = 'Abierta en nueva pestaña la optimización ' + rowIn.opti_id + ' para revisar cortes.';
  }

  private consumeHistoricalPdfResult() {
    if (this.ignoreHistoricalPdfResults) {
      localStorage.removeItem('vs_auto_historical_pdf_result');
      return;
    }
    const resultRaw = localStorage.getItem('vs_auto_historical_pdf_result');
    if (!resultRaw) {
      return;
    }

    localStorage.removeItem('vs_auto_historical_pdf_result');

    let result = null;
    try {
      result = JSON.parse(resultRaw);
    } catch (e) {
      return;
    }

    if (!result || !result.opti_id) {
      return;
    }

    if (!this.resultRows || this.resultRows.length === 0) {
      return;
    }

    const row = this.resultRows.find(item => Number(item.opti_id) === Number(result.opti_id));
    if (!row) {
      return;
    }

    if (result.mode === 'cnc_only') {
      this.processBulkCncResult(result);
      return;
    }

    if (result.groups) {
      row.pdf_groups_section = result.groups;
    }
    if (result.cutting) {
      row.pdf_cutting_section = result.cutting;
    }
    if (result.graphics) {
      row.pdf_graphics_section = result.graphics;
    }
    if (result.groups || result.cutting || result.graphics) {
      row.historical = false;
    }

    if (result.groups || result.cutting || result.graphics) {
      this.infoMessage = 'PDFs generados para la optimización ' + result.opti_id + '.';
    } else if (result.graphics_done) {
      this.infoMessage = 'PDF de Gráficos generado para la optimización ' + result.opti_id + '.';
    } else {
      this.errorMessage = 'No se pudieron generar los PDFs para la optimización ' + result.opti_id + '.';
    }

    this.persistHistoricalSummaryState();

    if (this.pendingRowPdfOpen &&
      this.pendingRowPdfOpen.type === 'graphics' &&
      Number(this.pendingRowPdfOpen.optiId) === Number(result.opti_id) &&
      row.pdf_graphics_section) {
      this.openPdf(row.pdf_graphics_section);
      this.pendingRowPdfOpen = null;
    }

    const bulkState = this.getBulkPdfState();
    if (bulkState && bulkState.running && bulkState.ids && bulkState.index < bulkState.ids.length) {
      const currentId = Number(bulkState.ids[bulkState.index]);
      if (currentId === Number(result.opti_id)) {
        this.touchBulkActivity();
        this.bulkProgressCurrent = this.bulkProgressCurrent + 1;
        if (bulkState.phase === 'groups') {
          bulkState.phase = 'graphics';
          this.setBulkPdfState(bulkState);
        } else {
          bulkState.index += 1;
          bulkState.phase = 'groups';
          this.setBulkPdfState(bulkState);
        }
        this.syncBulkProgressFromState();

        setTimeout(() => {
          this.processBulkPdfStep();
        }, 900);
      }
    }
  }

  private processBulkCncStep() {
    if (!this.bulkCncRunning || this.ignoreHistoricalPdfResults) {
      return;
    }
    this.startBulkHeartbeat();
    this.touchBulkActivity();

    if (!this.bulkCncIds || this.bulkCncIndex >= this.bulkCncIds.length) {
      this.bulkProgressLabel = 'Empaquetando CNCs en ZIP...';
      console.log('[AutoOpti][CNC] Fin de generación por fila. Total ficheros recolectados:', this.bulkCncCollectedFiles ? this.bulkCncCollectedFiles.length : 0);
      this.createAndDownloadCncZip().then(result => {
        const totalFiles = result.total_files;
        const zipName = result.zip_name;
        this.resetBulkCncState();
        this.bulkProgressVisible = false;
        if (result.ok && totalFiles > 0) {
          this.infoMessage = 'Descargado ZIP ' + zipName + ' con ' + totalFiles + ' fichero(s) CNC.';
        } else {
          this.errorMessage = 'No se pudo descargar el ZIP de CNCs.';
        }
      }).catch(() => {
        this.resetBulkCncState();
        this.bulkProgressVisible = false;
        this.errorMessage = 'No se pudo descargar el ZIP de CNCs.';
      });
      return;
    }

    const currentId = Number(this.bulkCncIds[this.bulkCncIndex]);
    console.log('[AutoOpti][CNC] Procesando opti_id:', currentId, 'indice:', this.bulkCncIndex + 1, '/', this.bulkCncIds.length);
    const row = this.resultRows.find(item => Number(item.opti_id) === currentId);
    if (!row) {
      this.bulkCncIndex += 1;
      this.bulkProgressCurrent = this.bulkCncIndex;
      setTimeout(() => this.processBulkCncStep(), 150);
      return;
    }

    this.bulkProgressLabel = 'Generando CNC ' + (this.bulkCncIndex + 1) + ' de ' + this.bulkCncIds.length + '...';
    this.infoMessage = 'Procesando exportación CNC para optimización ' + currentId + '...';
    this.ensureAutoRowByOptiId(currentId).then(() => {
      if (!this.bulkCncRunning) {
        return;
      }
      if (Number(this.bulkCncIds[this.bulkCncIndex]) !== currentId) {
        return;
      }
      const currentRow = this.resultRows.find(item => Number(item.opti_id) === currentId);
      if (!currentRow) {
        this.bulkCncIndex += 1;
        this.bulkProgressCurrent = this.bulkCncIndex;
        setTimeout(() => this.processBulkCncStep(), 150);
        return;
      }

      if (this.tryGenerateCncFromStoredSnapshot(currentId, currentRow)) {
        return;
      }
      this.openHistoricalOptiCnc(currentRow);
    });
  }

  private prefetchAutoRowsForCurrentResults() {
    if (!this.resultRows || this.resultRows.length < 1) {
      return;
    }

    for (let i = 0; i < this.resultRows.length; i++) {
      const row = this.resultRows[i];
      if (!row || !row.opti_id || row.opti_id === '-') {
        continue;
      }
      const idNum = Number(row.opti_id);
      if (!isNaN(idNum) && idNum > 0) {
        this.ensureAutoRowByOptiId(idNum);
      }
    }
  }

  private ensureAutoRowByOptiId(optiIdIn: number): Promise<any> {
    const optiId = Number(optiIdIn);
    if (!(optiId > 0)) {
      return Promise.resolve(null);
    }

    if (this.autoRowsById && this.autoRowsById[optiId]) {
      return Promise.resolve(this.autoRowsById[optiId]);
    }

    if (this.bulkCncAutoRowLoadPromises[optiId]) {
      return this.bulkCncAutoRowLoadPromises[optiId];
    }

    const params = {
      filterField_0: '',
      filterField_1: '',
      filterField_2: '',
      filterField_3: '',
      opti_id: optiId.toString(),
      opti_lot: '',
      opti_mat: '',
      opti_note: '',
      groupId: localStorage.getItem('group_id'),
      type: 0,
      f_date: null,
      f_date2: null
    };

    this.bulkCncAutoRowLoadPromises[optiId] = new Promise(resolve => {
      this.optiService.getAllOpti(params).subscribe(
        (response: any[]) => {
          let row = null;
          if (response && response.length > 0) {
            row = response.find(item => Number(item && item.id) === optiId);
            if (!row) {
              row = response[0];
            }
          }

          if (row) {
            this.autoRowsById[optiId] = row;
          }

          delete this.bulkCncAutoRowLoadPromises[optiId];
          resolve(row);
        },
        () => {
          delete this.bulkCncAutoRowLoadPromises[optiId];
          resolve(null);
        }
      );
    });

    return this.bulkCncAutoRowLoadPromises[optiId];
  }

  private tryGenerateCncFromStoredSnapshot(optiIdIn: number, rowIn: any): boolean {
    const payload = this.buildStoredSnapshotCncPayload(optiIdIn);
    if (!payload) {
      return false;
    }

    this.touchBulkActivity();
    this.bulkProgressLabel = 'Generando CNC ' + (this.bulkCncIndex + 1) + ' de ' + this.bulkCncIds.length + ' (snapshot)...';
    this.infoMessage = 'Generando CNC desde snapshot para optimización ' + optiIdIn + '...';
    console.log('[AutoOpti][CNC] Usando camino rápido por snapshot para opti_id:', optiIdIn);

    this.fileService.printEasels(payload).subscribe(
      (response: any) => {
        const cncFiles = this.extractCncFilesFromPrintResponse(response);
        if (cncFiles.length > 0) {
          this.processBulkCncResult({
            opti_id: optiIdIn,
            cnc_files: cncFiles
          });
          return;
        }

        console.warn('[AutoOpti][CNC] Snapshot sin ficheros válidos. Fallback al flujo histórico para opti_id:', optiIdIn);
        this.openHistoricalOptiCnc(rowIn);
      },
      () => {
        console.warn('[AutoOpti][CNC] Error en camino snapshot. Fallback al flujo histórico para opti_id:', optiIdIn);
        this.openHistoricalOptiCnc(rowIn);
      }
    );

    return true;
  }

  private buildStoredSnapshotCncPayload(optiIdIn: number): any {
    const optiData = this.autoRowsById && this.autoRowsById[optiIdIn] ? this.autoRowsById[optiIdIn] : null;
    if (!optiData || !optiData.od_layout_snapshot) {
      return null;
    }

    let snapshot = null;
    try {
      snapshot = JSON.parse(optiData.od_layout_snapshot);
    } catch (e) {
      return null;
    }

    if (!snapshot) {
      return null;
    }

    const form = snapshot.form ? snapshot.form : {};
    const exportModeRaw = form.export !== undefined && form.export !== null ? form.export.toString().toUpperCase() : 'V40';
    const exportMode = ['V40', 'LSW', 'LIOPT', 'OTD', 'BTR', 'ELO', 'TEC'].includes(exportModeRaw) ? exportModeRaw : 'V40';
    if (exportMode === 'LSW' || exportMode === 'LIOPT') {
      // El payload "snapshot" de LSW ha mostrado incompatibilidades con el simulador.
      // Forzamos fallback al flujo histórico (openHistoricalOptiCnc), que usa el mismo
      // camino de generación estable de versiones anteriores.
      return null;
    }

    const sheetList = Array.isArray(snapshot.opti_sheet_list) ? snapshot.opti_sheet_list : [];
    const level0TraverseList = this.normalizeSnapshotTraverseList(snapshot.level0_traverse_list);
    if (sheetList.length < 1) {
      return null;
    }

    let action = 'CreateV40';
    let sheetListToExport = sheetList;

    if (exportMode === 'LSW' || exportMode === 'LIOPT') {
      action = exportMode === 'LIOPT' ? 'CreateLIOPT' : 'CreateLSW';
      sheetListToExport = level0TraverseList;
    } else if (exportMode === 'OTD') {
      action = 'CreateOTD';
      sheetListToExport = level0TraverseList;
    } else if (exportMode === 'BTR') {
      action = 'CreateBTR';
      sheetListToExport = level0TraverseList;
    } else if (exportMode === 'ELO') {
      action = 'CreateELO';
      sheetListToExport = level0TraverseList;
    } else if (exportMode === 'TEC') {
      action = 'CreateTEC';
      sheetListToExport = level0TraverseList;
    }

    if (!sheetListToExport || sheetListToExport.length < 1) {
      return null;
    }

    const metrics = snapshot.metrics ? snapshot.metrics : {};
    const toNum = (valueIn: any, fallbackIn: number = 0): number => {
      const value = Number(valueIn);
      return Number.isFinite(value) ? value : fallbackIn;
    };

    return {
      group_list: this.normalizeSnapshotGroupList(snapshot.opti_group_info_list),
      sheet_list: sheetListToExport,
      opti_id: Number(optiIdIn) > 0 ? Number(optiIdIn) : null,
      action: action,
      back: 'auto-opti-lote',
      mode: toNum(form.mode, 2),
      level: toNum(form.level, 1),
      origin: this.normalizeOriginReference(form.origin),
      merma: Number(toNum(metrics.total_area_x, 0).toFixed(2)) * 100,
      bruto: Number(toNum(metrics.total_area_b, 0).toFixed(2)) * 100,
      neto: Number(toNum(metrics.total_area_p, 0).toFixed(2)) * 100,
      block: !!form.blocks
    };
  }

  private normalizeOriginReference(originIn: any): number {
    const numericOrigin = Number(originIn);
    if (Number.isFinite(numericOrigin) && numericOrigin >= 1 && numericOrigin <= 4) {
      return numericOrigin;
    }

    const origin = originIn !== undefined && originIn !== null
      ? originIn.toString().toUpperCase().trim()
      : '';
    if (origin === 'LT') return 1;
    if (origin === 'LB') return 2;
    if (origin === 'RB') return 3;
    if (origin === 'RT') return 4;
    return 1;
  }

  private normalizeSnapshotGroupList(groupInfoIn: any): any[] {
    const listOut = [];
    if (!groupInfoIn) {
      return listOut;
    }

    if (Array.isArray(groupInfoIn)) {
      for (let i = 0; i < groupInfoIn.length; i++) {
        const item = groupInfoIn[i];
        if (!item) {
          continue;
        }
        if (item.name !== undefined && item.content !== undefined) {
          listOut.push({ name: item.name, content: item.content });
          continue;
        }
        listOut.push({ name: i.toString(), content: item });
      }
      return listOut;
    }

    for (const prop in groupInfoIn) {
      if (groupInfoIn.hasOwnProperty(prop)) {
        listOut.push({ name: prop, content: groupInfoIn[prop] });
      }
    }
    return listOut;
  }

  private normalizeSnapshotTraverseList(traverseIn: any): any[] {
    if (!traverseIn) {
      return [];
    }
    if (Array.isArray(traverseIn)) {
      return traverseIn;
    }
    const listOut = [];
    for (const prop in traverseIn) {
      if (traverseIn.hasOwnProperty(prop)) {
        listOut.push(traverseIn[prop]);
      }
    }
    return listOut;
  }

  private extractCncFilesFromPrintResponse(responseIn: any): any[] {
    const filesOut = [];
    if (!responseIn || !responseIn.action) {
      return filesOut;
    }

    if (responseIn.action === 'CreateV40') {
      if (responseIn.file_name && responseIn.turomas_JSON) {
        filesOut.push({
          file_name: responseIn.file_name,
          content: responseIn.turomas_JSON,
          mime_type: 'application/json;charset=utf-8'
        });
      }
      return filesOut;
    }

    if (responseIn.action === 'CreateOTD') {
      if (responseIn.file_name && responseIn.intermac_OTD) {
        filesOut.push({
          file_name: responseIn.file_name,
          content: responseIn.intermac_OTD,
          mime_type: 'text/plain;charset=utf-8'
        });
      }
      return filesOut;
    }

    if (responseIn.action === 'CreateLSW' || responseIn.action === 'CreateLIOPT') {
      if (responseIn.file_name) {
        filesOut.push({
          file_name: responseIn.file_name,
          asset_url: '/assets/attachments/lisec/' + responseIn.file_name
        });
      }
      return filesOut;
    }

    if (responseIn.action === 'CreateBTR') {
      if (responseIn.content && responseIn.file_name && responseIn.content.tra1 && responseIn.file_name.tra1) {
        filesOut.push({
          file_name: responseIn.file_name.tra1,
          content: responseIn.content.tra1,
          mime_type: 'text/plain;charset=utf-8'
        });
      }
      return filesOut;
    }

    if (responseIn.action === 'CreateTEC') {
      if (responseIn.file_name && responseIn.content) {
        filesOut.push({
          file_name: responseIn.file_name,
          content: responseIn.content,
          mime_type: 'text/plain;charset=utf-8'
        });
      }
      return filesOut;
    }

    if (responseIn.action === 'CreateBendingCNC') {
      if (responseIn.file_name && responseIn.content) {
        filesOut.push({
          file_name: responseIn.file_name,
          content: responseIn.content,
          mime_type: 'text/plain;charset=utf-8'
        });
      }
      return filesOut;
    }

    if (responseIn.action === 'CreateELO') {
      if (responseIn.content && responseIn.file_name) {
        if (responseIn.content.nst && responseIn.file_name.nst) {
          filesOut.push({
            file_name: responseIn.file_name.nst,
            content: responseIn.content.nst,
            mime_type: 'text/plain;charset=utf-8'
          });
        }
        if (responseIn.content.cmt && responseIn.file_name.cmt) {
          filesOut.push({
            file_name: responseIn.file_name.cmt,
            content: responseIn.content.cmt,
            mime_type: 'text/plain;charset=utf-8'
          });
        }
        if (responseIn.content.dcs && responseIn.file_name.dcs) {
          filesOut.push({
            file_name: responseIn.file_name.dcs,
            content: responseIn.content.dcs,
            mime_type: 'text/plain;charset=utf-8'
          });
        }
      }
    }

    return filesOut;
  }

  private processBulkCncResult(resultIn: any) {
    if (!this.bulkCncRunning || this.ignoreHistoricalPdfResults) {
      return;
    }

    const currentId = Number(this.bulkCncIds[this.bulkCncIndex]);
    if (currentId !== Number(resultIn.opti_id)) {
      return;
    }
    this.touchBulkActivity();

    const cncFiles = resultIn && resultIn.cnc_files && Array.isArray(resultIn.cnc_files)
      ? resultIn.cnc_files
      : [];
    console.log('[AutoOpti][CNC] Resultado opti_id', currentId, '- archivos recibidos:', cncFiles.length);
    const normalized = this.normalizeCncFilesForZip(currentId, cncFiles);
    this.bulkCncCollectedFiles = this.bulkCncCollectedFiles.concat(normalized);
    this.bulkCncFilesDownloaded = this.bulkCncCollectedFiles.length;
    this.bulkCncIndex += 1;
    this.bulkProgressCurrent = this.bulkCncIndex;
    setTimeout(() => this.processBulkCncStep(), 250);
  }

  private normalizeCncFilesForZip(optiIdIn: number, filesIn: any[]): any[] {
    if (!filesIn || filesIn.length === 0) {
      return [];
    }

    const out = [];
    for (let i = 0; i < filesIn.length; i++) {
      const file = filesIn[i];
      if (!file) {
        continue;
      }
      const rawName = file.file_name ? file.file_name.toString() : ('export_' + (i + 1).toString() + '.cnc');
      const safeName = this.sanitizeFileName(rawName);
      const isZipAsset = !!(file.asset_url && safeName.toLowerCase().endsWith('.zip'));

      out.push({
        file_name: safeName,
        content: file.content !== undefined ? file.content : null,
        base64_content: file.base64_content ? file.base64_content : null,
        asset_url: file.asset_url ? file.asset_url : null,
        mime_type: file.mime_type ? file.mime_type : null,
        extract_zip_from_asset: isZipAsset
      });
    }
    return out;
  }

  private sanitizeFileName(fileNameIn: string): string {
    if (!fileNameIn) {
      return 'archivo.dat';
    }
    const cleaned = fileNameIn.toString().replace(/[^a-zA-Z0-9._-]/g, '_');
    return cleaned !== '' ? cleaned : 'archivo.dat';
  }

  private async createAndDownloadCncZip(): Promise<any> {
    let files = this.bulkCncCollectedFiles ? this.bulkCncCollectedFiles.slice() : [];
    const bendingFiles = await this.collectBendingCncBundleFiles();
    if (bendingFiles.length > 0) {
      files = files.concat(bendingFiles);
    }

    if (!files || files.length === 0) {
      return { ok: false, total_files: 0, zip_name: this.buildBulkZipName('cncs') };
    }

    const zipName = this.buildBulkZipName('cncs');
    const payload = {
      action: 'CreateZipBundle',
      back: 'auto-opti-lote',
      zip_name: zipName,
      files: files
    };

    const response: any = await new Promise(resolve => {
      this.fileService.printEasels(payload).subscribe(
        (res: any) => resolve(res),
        () => resolve(null)
      );
    });

    if (!response || !response.section) {
      return { ok: false, total_files: files.length, zip_name: zipName };
    }

    const ok = await this.downloadPdf(response.section);
    return {
      ok: ok,
      total_files: files.length,
      zip_name: zipName
    };
  }

  private async collectBendingCncBundleFiles(): Promise<any[]> {
    const out: any[] = [];
    const lotList = this.parseLotNumbers(this.lotNumber);
    if (!lotList || lotList.length < 1) {
      return out;
    }

    const machineId = Number(this.selectedBendingMachineId);
    if (!Number.isFinite(machineId) || machineId < 1) {
      return out;
    }

    const selectedMachine = this.bendingMachines && this.bendingMachines.length
      ? this.bendingMachines.find(item => Number(item && item.id) === machineId)
      : null;
    const stdDiscount = Number(selectedMachine && selectedMachine.std_profile_discount);
    if (!selectedMachine || !Number.isFinite(stdDiscount) || stdDiscount <= 0) {
      return out;
    }

    const payload: any = {
      action: 'CreateBendingCNC',
      back: 'auto-opti-lote',
      bending_info: {
        lot_list: lotList.map(item => item.toString()),
        lots: lotList.join(','),
        machine_id: machineId
      }
    };

    const response: any = await new Promise(resolve => {
      this.fileService.printEasels(payload).subscribe(
        (res: any) => resolve(res),
        () => resolve(null)
      );
    });

    // Si faltan códigos manuales, no bloqueamos la descarga masiva de CNCs.
    if (!response || response.manual_required === true) {
      return out;
    }

    const files = this.extractCncFilesFromPrintResponse(response);
    if (!files || files.length < 1) {
      return out;
    }

    const normalized = this.normalizeCncFilesForZip(0, files);
    for (let i = 0; i < normalized.length; i++) {
      out.push(normalized[i]);
    }
    return out;
  }

  private buildBulkZipName(typeIn: string): string {
    const lots = this.parseLotNumbers(this.lotNumber);
    let prefix = 'lote_sin_numero';
    if (lots.length === 1) {
      prefix = 'lote' + lots[0];
    } else if (lots.length > 1) {
      prefix = 'lotes' + lots.join('-');
    }

    if (typeIn === 'cncs') {
      return prefix + '-cncs.zip';
    }
    return prefix + '_pdfs.zip';
  }

  private resetBulkCncState() {
    this.bulkCncRunning = false;
    this.bulkCncIds = [];
    this.bulkCncIndex = 0;
    this.bulkCncFilesDownloaded = 0;
    this.bulkCncCollectedFiles = [];
  }

  private syncBulkProgressFromState() {
    const state = this.getBulkPdfState();
    if (!state || !state.running || !state.ids || state.ids.length === 0) {
      if (this.bulkCncRunning) {
        return;
      }
      this.bulkProgressVisible = false;
      this.stopBulkHeartbeat();
      return;
    }

    this.startBulkHeartbeat();
    this.bulkProgressVisible = true;
    this.bulkProgressTotal = state.ids.length * 2;
    const completed = (state.index * 2) + (state.phase === 'graphics' ? 1 : 0);
    this.bulkProgressCurrent = completed;
    this.bulkProgressLabel = state.phase === 'groups'
      ? 'Pendiente descarga de BD del PDF de Caballetes...'
      : 'Pendiente descarga de BD del PDF de Gráficos...';
  }

  private startHistoricalLinkPrefetch() {
    if (!this.resultRows || this.resultRows.length === 0) {
      return;
    }

    const bulkState = this.getBulkPdfState();
    if (bulkState && bulkState.running) {
      return;
    }

    const ids = this.resultRows
      .filter(row => row && row.opti_id && row.opti_id !== '-')
      .map(row => Number(row.opti_id))
      .filter(id => !isNaN(id) && id > 0);

    if (ids.length === 0) {
      return;
    }

    this.setBulkPdfState({
      running: true,
      index: 0,
      phase: 'groups',
      ids: ids,
      purpose: 'prepare_links'
    });

    this.bulkProgressVisible = true;
    this.bulkProgressCurrent = 0;
    this.bulkProgressTotal = ids.length * 2;
    this.bulkProgressLabel = 'Descargando de BD el PDF de Caballetes y Gráficos...';
    this.startBulkHeartbeat();

    this.processBulkPdfStep();
  }

  private hasReadyAllPdfLinks(rowIn: any): boolean {
    if (!rowIn) {
      return false;
    }
    return !!(rowIn.pdf_groups_section && rowIn.pdf_graphics_section);
  }

  private async tryHydrateGroupsAndCuttingFromCache(rowIn: any, includeCuttingIn: boolean = false): Promise<boolean> {
    if (!rowIn || !rowIn.opti_id || rowIn.opti_id === '-') {
      return false;
    }

    const optiIdNum = Number(rowIn.opti_id);
    if (isNaN(optiIdNum) || optiIdNum <= 0) {
      return false;
    }

    let changed = false;

    if (!rowIn.pdf_groups_section) {
      const payloadGroups = {
        action: 'PrintFile2',
        back: 'auto-opti-lote',
        opti_id: optiIdNum,
        opti_info: { id: optiIdNum }
      };
      const responseGroups = await this.callPrintEasels(payloadGroups);
      if (responseGroups && responseGroups.section) {
        rowIn.pdf_groups_section = responseGroups.section;
        changed = true;
      }
    }

    if (includeCuttingIn && !rowIn.pdf_cutting_section) {
      const payloadCutting = {
        action: 'PrintCuttingMode2',
        back: 'auto-opti-lote',
        opti_id: optiIdNum,
        opti_info: { id: optiIdNum }
      };
      const responseCutting = await this.callPrintEasels(payloadCutting);
      if (responseCutting && responseCutting.section) {
        rowIn.pdf_cutting_section = responseCutting.section;
        changed = true;
      }
    }

    if (changed) {
      rowIn.historical = false;
      this.persistHistoricalSummaryState();
    }

    if (includeCuttingIn) {
      return !!(rowIn.pdf_groups_section && rowIn.pdf_cutting_section);
    }
    return !!rowIn.pdf_groups_section;
  }

  private async tryHydrateGraphicsFromCache(rowIn: any): Promise<boolean> {
    if (!rowIn || !rowIn.opti_id || rowIn.opti_id === '-') {
      return false;
    }

    if (rowIn.pdf_graphics_section) {
      return true;
    }

    const optiIdNum = Number(rowIn.opti_id);
    if (isNaN(optiIdNum) || optiIdNum <= 0) {
      return false;
    }

    const payloadGraphics = {
      action: 'PrintFile',
      back: 'auto-opti-lote',
      opti_id: optiIdNum,
      opti_info: { id: optiIdNum }
    };

    const responseGraphics = await this.callPrintSheets(payloadGraphics);
    if (responseGraphics && responseGraphics.section) {
      rowIn.pdf_graphics_section = responseGraphics.section;
      rowIn.historical = false;
      this.persistHistoricalSummaryState();
      return true;
    }

    return false;
  }

  private callPrintEasels(payloadIn: any): Promise<any> {
    return new Promise(resolve => {
      this.fileService.printEasels(payloadIn).subscribe(
        (response: any) => resolve(response),
        () => resolve(null)
      );
    });
  }

  private callPrintSheets(payloadIn: any): Promise<any> {
    return new Promise(resolve => {
      this.fileService.printSheets(payloadIn).subscribe(
        (response: any) => resolve(response),
        () => resolve(null)
      );
    });
  }

  private advanceBulkStateForCurrentRow(currentIdIn: number) {
    const bulkState = this.getBulkPdfState();
    if (!bulkState || !bulkState.running || !bulkState.ids || bulkState.index >= bulkState.ids.length) {
      return;
    }

    const currentId = Number(bulkState.ids[bulkState.index]);
    if (currentId !== Number(currentIdIn)) {
      return;
    }

    this.touchBulkActivity();
    this.bulkProgressCurrent = this.bulkProgressCurrent + 1;
    if (bulkState.phase === 'groups') {
      bulkState.phase = 'graphics';
      this.setBulkPdfState(bulkState);
    } else {
      bulkState.index += 1;
      bulkState.phase = 'groups';
      this.setBulkPdfState(bulkState);
    }
    this.syncBulkProgressFromState();

    setTimeout(() => {
      this.processBulkPdfStep();
    }, 120);
  }

  private persistHistoricalSummaryState() {
    const payload = {
      lotNumber: this.lotNumber ? this.lotNumber : '',
      resultRows: this.resultRows ? this.resultRows : [],
      autoRowsById: this.autoRowsById ? this.autoRowsById : {}
    };
    localStorage.setItem('vs_auto_historical_summary_state', JSON.stringify(payload));
  }

  private hasPendingAutoExecutionContext(): boolean {
    const continueFlag = localStorage.getItem('vs_auto_batch_continue');
    const currentJob = localStorage.getItem('vs_auto_batch_current_job');
    const lastResult = localStorage.getItem('vs_auto_batch_last_result');
    const historicalJob = localStorage.getItem('vs_auto_historical_pdf_job');
    const bulkStateRaw = localStorage.getItem(this.bulkPdfStateKey);

    if (continueFlag === '1' || currentJob || lastResult || historicalJob) {
      return true;
    }

    if (bulkStateRaw) {
      try {
        const bulkState = JSON.parse(bulkStateRaw);
        if (bulkState && bulkState.running) {
          return true;
        }
      } catch (e) {
        return false;
      }
    }

    return false;
  }

  private cleanupStaleAutoContext() {
    const now = Date.now();
    const maxAgeMs = 10 * 60 * 1000;

    const historicalJobRaw = localStorage.getItem('vs_auto_historical_pdf_job');
    if (historicalJobRaw) {
      try {
        const historicalJob = JSON.parse(historicalJobRaw);
        const createdAt = historicalJob && historicalJob.created_at ? Number(historicalJob.created_at) : 0;
        if (createdAt > 0 && (now - createdAt) > maxAgeMs) {
          localStorage.removeItem('vs_auto_historical_pdf_job');
        }
      } catch (e) {
        localStorage.removeItem('vs_auto_historical_pdf_job');
      }
    }

    const bulkRaw = localStorage.getItem(this.bulkPdfStateKey);
    if (bulkRaw) {
      try {
        const bulk = JSON.parse(bulkRaw);
        const updatedAt = bulk && bulk.updated_at ? Number(bulk.updated_at) : 0;
        if (bulk && bulk.running && updatedAt > 0 && (now - updatedAt) > maxAgeMs) {
          localStorage.removeItem(this.bulkPdfStateKey);
        }
      } catch (e) {
        localStorage.removeItem(this.bulkPdfStateKey);
      }
    }

    const resumeHintRaw = localStorage.getItem('vs_auto_batch_resume_hint');
    if (resumeHintRaw) {
      try {
        const resumeHint = JSON.parse(resumeHintRaw);
        const ts = resumeHint && resumeHint.ts ? Number(resumeHint.ts) : 0;
        if (ts <= 0 || (now - ts) > maxAgeMs) {
          localStorage.removeItem('vs_auto_batch_resume_hint');
        }
      } catch (e) {
        localStorage.removeItem('vs_auto_batch_resume_hint');
      }
    }
  }

  private hardResetAutoContext() {
    this.stopBulkHeartbeat();
    this.cancelPendingRequests();
    this.clearIgnoreHistoricalPdfResultsTimer();
    this.resetBulkCncState();
    localStorage.removeItem('vs_auto_batch_continue');
    localStorage.removeItem('vs_auto_batch_current_job');
    localStorage.removeItem('vs_auto_batch_last_result');
    localStorage.removeItem('vs_auto_batch_queue');
    localStorage.removeItem('vs_auto_batch_index');
    localStorage.removeItem('vs_auto_batch_results');
    localStorage.removeItem('vs_auto_batch_started_at');
    localStorage.removeItem('vs_auto_batch_job_started_at');
    localStorage.removeItem('vs_auto_batch_local_hb');
    localStorage.removeItem('vs_auto_batch_backend_hb');
    localStorage.removeItem('vs_auto_historical_pdf_job');
    localStorage.removeItem('vs_auto_historical_pdf_result');
    localStorage.removeItem('vs_auto_batch_resume_hint');
    localStorage.removeItem(this.bulkPdfStateKey);
    localStorage.removeItem('vs_auto_historical_summary_state');
    const frame = document.getElementById(this.historicalPdfWorkerFrameId);
    if (frame && frame.parentNode) {
      frame.parentNode.removeChild(frame);
    }
  }

  private clearTransientAutoState() {
    this.lotNumber = '';
    this.errorMessage = '';
    this.infoMessage = '';
    this.executionProgress = '';
    this.materialRows = [];
    this.resultRows = [];
    this.resultPage = 1;
    this.floorPiecesByMaterial = {};
    this.autoRowsById = {};
    this.selectedLots = [];
    this.loadingMaterials = false;
    this.runningBatch = false;
    this.runningRealBatch = false;
    this.loadingLastSummary = false;
    this.loadingFloorPicker = false;
    this.deletingWarningOptis = false;
    this.printingCompactSummary = false;
    this.resetBulkCncState();
    localStorage.removeItem('vs_auto_historical_summary_state');
  }

  private hasActiveAutoProcess(): boolean {
    const bulkState = this.getBulkPdfState();
    return !!(
      this.loadingMaterials ||
      this.loadingLastSummary ||
      this.loadingFloorPicker ||
      this.runningBatch ||
      this.runningRealBatch ||
      this.deletingWarningOptis ||
      this.printingCompactSummary ||
      this.bulkCncRunning ||
      (this.bulkProgressVisible && this.bulkProgressTotal > 0) ||
      (bulkState && bulkState.running) ||
      localStorage.getItem('vs_auto_batch_continue') === '1' ||
      !!localStorage.getItem('vs_auto_historical_pdf_job')
    );
  }

  private cancelActiveAutoFlows() {
    this.stopBulkHeartbeat();
    this.cancelPendingRequests();
    this.ignoreHistoricalPdfResults = true;
    this.clearIgnoreHistoricalPdfResultsTimer();
    this.ignoreHistoricalPdfResultsTimer = setTimeout(() => {
      this.ignoreHistoricalPdfResults = false;
      this.ignoreHistoricalPdfResultsTimer = null;
    }, 15000);

    this.runningBatch = false;
    this.runningRealBatch = false;
    this.loadingMaterials = false;
    this.loadingLastSummary = false;
    this.loadingFloorPicker = false;
    this.deletingWarningOptis = false;
    this.printingCompactSummary = false;
    this.executionProgress = '';

    this.bulkProgressVisible = false;
    this.bulkProgressCurrent = 0;
    this.bulkProgressTotal = 0;
    this.bulkProgressLabel = '';
    this.clearBulkPdfState();
    this.resetBulkCncState();

    localStorage.removeItem('vs_auto_batch_continue');
    localStorage.removeItem('vs_auto_batch_current_job');
    localStorage.removeItem('vs_auto_batch_queue');
    localStorage.removeItem('vs_auto_batch_index');
    localStorage.removeItem('vs_auto_batch_started_at');
    localStorage.removeItem('vs_auto_batch_job_started_at');
    localStorage.removeItem('vs_auto_batch_local_hb');
    localStorage.removeItem('vs_auto_batch_backend_hb');
    localStorage.removeItem('vs_auto_batch_resume_hint');
    localStorage.removeItem('vs_auto_historical_pdf_job');
    localStorage.removeItem('vs_auto_historical_pdf_result');

    const frame = document.getElementById(this.historicalPdfWorkerFrameId);
    if (frame && frame.parentNode) {
      frame.parentNode.removeChild(frame);
    }
  }

  private cancelPendingRequests() {
    this.cancelSubscription('pendingFloorPiecesRequest');
    this.cancelSubscription('pendingLastSummaryRequest');
    this.cancelSubscription('pendingFloorPickerRequest');
  }

  private cancelSubscription(fieldNameIn: string) {
    const sub = this[fieldNameIn];
    if (sub && typeof sub.unsubscribe === 'function') {
      sub.unsubscribe();
    }
    this[fieldNameIn] = null;
  }

  private clearIgnoreHistoricalPdfResultsTimer() {
    if (this.ignoreHistoricalPdfResultsTimer) {
      clearTimeout(this.ignoreHistoricalPdfResultsTimer);
      this.ignoreHistoricalPdfResultsTimer = null;
    }
    this.ignoreHistoricalPdfResults = false;
  }

  private restoreHistoricalSummaryState() {
    const raw = localStorage.getItem('vs_auto_historical_summary_state');
    if (!raw) {
      return;
    }

    try {
      const state = JSON.parse(raw);
      if (state && state.lotNumber) {
        this.lotNumber = state.lotNumber.toString();
      }
      if (state && state.resultRows && state.resultRows.length) {
        this.resultRows = state.resultRows;
        this.resultPage = 1;
      }
      if (state && state.autoRowsById) {
        this.autoRowsById = state.autoRowsById;
      }
    } catch (e) {
      // Si falla el parseo, no restauramos estado.
    }
  }

  private persistCompletedBatchHistory() {
    const lot = this.lotNumber ? this.lotNumber.trim() : '';
    if (!lot) {
      return;
    }

    const resultsRaw = localStorage.getItem('vs_auto_batch_results');
    if (!resultsRaw) {
      return;
    }

    let results = [];
    try {
      results = JSON.parse(resultsRaw);
    } catch (e) {
      return;
    }

    if (!results || results.length === 0) {
      return;
    }

    const historyRaw = localStorage.getItem('vs_auto_batch_history');
    let history = [];
    if (historyRaw) {
      try {
        history = JSON.parse(historyRaw);
      } catch (e) {
        history = [];
      }
    }

    history.push({
      lot: lot,
      executedAt: new Date().getTime(),
      results: results
    });

    if (history.length > 25) {
      history = history.slice(history.length - 25);
    }

    localStorage.setItem('vs_auto_batch_history', JSON.stringify(history));
  }

  private formatExecutionDate(dateIn: any): string {
    if (!dateIn) {
      return '-';
    }
    const dateAux = new Date(Number(dateIn));
    if (!dateAux || isNaN(dateAux.getTime())) {
      return '-';
    }
    const year = dateAux.getFullYear().toString();
    const month = (dateAux.getMonth() + 1).toString().padStart(2, '0');
    const day = dateAux.getDate().toString().padStart(2, '0');
    const hour = dateAux.getHours().toString().padStart(2, '0');
    const minute = dateAux.getMinutes().toString().padStart(2, '0');
    return year + '-' + month + '-' + day + ' ' + hour + ':' + minute;
  }

  private formatDateTimeHuman(dateIn: Date): string {
    if (!dateIn || isNaN(dateIn.getTime())) {
      return '-';
    }
    const day = dateIn.getDate().toString().padStart(2, '0');
    const month = (dateIn.getMonth() + 1).toString().padStart(2, '0');
    const year = dateIn.getFullYear().toString();
    const hour = dateIn.getHours().toString().padStart(2, '0');
    const minute = dateIn.getMinutes().toString().padStart(2, '0');
    return day + '/' + month + '/' + year + ' ' + hour + ':' + minute;
  }

  private normalizeSummaryText(valueIn: any, fallbackIn: string): string {
    if (valueIn === null || valueIn === undefined) {
      return fallbackIn;
    }
    const text = valueIn.toString().trim();
    return text !== '' ? text : fallbackIn;
  }

  private extractLoggedUserName(): string {
    const directName = this.normalizeSummaryText(localStorage.getItem('name'), '');
    if (directName !== '') {
      return directName;
    }

    const directUsername = this.normalizeSummaryText(localStorage.getItem('username'), '');
    if (directUsername !== '') {
      return directUsername;
    }

    const currentUserRaw = localStorage.getItem('currentUser');
    if (currentUserRaw) {
      try {
        const currentUser = JSON.parse(currentUserRaw);
        const currentUserName = this.normalizeSummaryText(currentUser && currentUser.name ? currentUser.name : '', '');
        if (currentUserName !== '') {
          return currentUserName;
        }
        const currentUserUsername = this.normalizeSummaryText(currentUser && currentUser.username ? currentUser.username : '', '');
        if (currentUserUsername !== '') {
          return currentUserUsername;
        }
        const currentUserEmail = this.normalizeSummaryText(currentUser && currentUser.email ? currentUser.email : '', '');
        if (currentUserEmail !== '') {
          return currentUserEmail;
        }
      } catch (e) {
        // Si falla el parseado, seguimos con fallback.
      }
    }

    return '-';
  }

  getTotalOptimizationSeconds(): number {
    if (!this.resultRows || this.resultRows.length === 0) {
      return 0;
    }
    let total = 0;
    for (let i = 0; i < this.resultRows.length; i++) {
      const seconds = Number(this.resultRows[i].optimization_time_seconds);
      if (!isNaN(seconds) && seconds > 0) {
        total += seconds;
      }
    }
    return Number(total.toFixed(2));
  }

  getTotalOptimizationTime(): string {
    const totalSeconds = this.getTotalOptimizationSeconds();
    if (totalSeconds <= 0) {
      return '-';
    }
    return this.formatSecondsAsDuration(totalSeconds);
  }

  private getSpeedLabel(speedIn: string): string {
    if (speedIn === 'optimal') {
      return 'Óptima';
    }
    if (speedIn === 'medium') {
      return 'Media';
    }
    return 'Rápida';
  }

  private formatSecondsAsDuration(totalSecondsIn: number): string {
    const safeTotal = totalSecondsIn > 0 ? totalSecondsIn : 0;
    const wholeSeconds = Math.floor(safeTotal);
    const hours = Math.floor(wholeSeconds / 3600);
    const minutes = Math.floor((wholeSeconds % 3600) / 60);
    const seconds = wholeSeconds % 60;
    return this.pad2(hours) + ':' + this.pad2(minutes) + ':' + this.pad2(seconds);
  }

  private pad2(valueIn: number): string {
    return valueIn.toString().padStart(2, '0');
  }

  private getMaxOptiId(listIn: any[]): number {
    if (!listIn || listIn.length === 0) {
      return 0;
    }
    let maxId = 0;
    for (let i = 0; i < listIn.length; i++) {
      const idAux = listIn[i] && listIn[i].id ? Number(listIn[i].id) : 0;
      if (idAux > maxId) {
        maxId = idAux;
      }
    }
    return maxId;
  }

  private extractMaterialDesc(valueIn: any): string {
    if (!valueIn) {
      return '';
    }
    try {
      const parsed = JSON.parse(valueIn);
      if (parsed && parsed.length > 0) {
        return parsed[0].toString();
      }
    } catch (e) {
      return valueIn.toString();
    }
    return valueIn.toString();
  }

  private extractMaterialId(valueIn: any): string {
    if (!valueIn) {
      return '';
    }
    try {
      const parsed = JSON.parse(valueIn);
      if (parsed && parsed.length > 0) {
        return parsed[0].toString();
      }
    } catch (e) {
      return valueIn.toString();
    }
    return valueIn.toString();
  }

  private extractStrategy(configurationIn: any): string {
    if (!configurationIn) {
      return '-';
    }
    const cfg = configurationIn.toString().split(',');
    if (!cfg || cfg.length < 2) {
      return '-';
    }
    const mode = Number(cfg[1]);
    if (mode === 3) {
      return 'menos pilas';
    }
    return 'menos merma';
  }

  private extractLevelFromConfiguration(configurationIn: any): any {
    if (!configurationIn) {
      return '-';
    }
    const cfg = configurationIn.toString().split(',');
    if (!cfg || cfg.length < 1) {
      return '-';
    }
    const level = Number(cfg[0]);
    if (!isNaN(level) && level > 0) {
      return level;
    }
    return '-';
  }

  private formatStrategyLabel(strategyIn: string): string {
    if (!strategyIn) {
      return '-';
    }

    const value = strategyIn.toString().toLowerCase();
    if (value === 'less_waste' || value === 'menos merma') {
      return 'Menos merma';
    }
    if (value === 'less_stacks' || value === 'menos pilas') {
      return 'Menos pilas';
    }
    return strategyIn;
  }

  private getLotsUsageLabel(lotsUsedIn: any, lotScopeIn: any): string {
    const lotsUsed = this.normalizeLotsList(lotsUsedIn);
    const lotScope = this.normalizeLotsList(lotScopeIn);

    if (lotsUsed.length === 0 && lotScope.length === 0) {
      return '-';
    }

    if (lotsUsed.length === 0) {
      return lotScope.length > 1 ? ('0/' + lotScope.length) : lotScope[0];
    }

    if (lotScope.length === 0) {
      return lotsUsed.join(', ');
    }

    return lotsUsed.length + '/' + lotScope.length + ' (' + lotsUsed.join(', ') + ')';
  }

  private normalizeLotsList(lotsIn: any): string[] {
    if (lotsIn === undefined || lotsIn === null) {
      return [];
    }

    if (Array.isArray(lotsIn)) {
      return Array.from(new Set(
        lotsIn
          .map(item => item !== undefined && item !== null ? item.toString().trim() : '')
          .filter(item => item !== '')
      ));
    }

    return Array.from(new Set(
      lotsIn
        .toString()
        .split(',')
        .map(item => item.trim())
        .filter(item => item !== '')
    ));
  }

  private extractLotsFromHistorical(lotsFieldIn: any): string[] {
    if (lotsFieldIn === undefined || lotsFieldIn === null) {
      return [];
    }

    const text = lotsFieldIn.toString();
    const matches = text.match(/[0-9]+/g);
    if (!matches || matches.length === 0) {
      return [];
    }

    return Array.from(new Set(matches.map(item => item.trim()).filter(item => item !== '')));
  }

  private selectLatestRowsByMaterial(rowsIn: any[]): any[] {
    if (!rowsIn || rowsIn.length === 0) {
      return [];
    }

    const ordered = rowsIn
      .slice(0)
      .sort((a, b) => Number(b.id) - Number(a.id));

    const materialMap = {};
    const result = [];

    for (let i = 0; i < ordered.length; i++) {
      const row = ordered[i];
      const materialKey = this.extractMaterialDesc(row.od_product_desc);
      if (!materialKey) {
        continue;
      }

      if (!materialMap[materialKey]) {
        materialMap[materialKey] = true;
        result.push(row);
      }
    }

    return result;
  }

  private getCompactLotListFromCurrentContext(): string[] {
    let lotList = this.parseLotNumbers(this.lotNumber);

    if (lotList.length === 0 && this.selectedLots && this.selectedLots.length > 0) {
      lotList = this.selectedLots.slice(0);
    }

    if (lotList.length === 0 && this.resultRows && this.resultRows.length > 0) {
      const lotAux = [];
      this.resultRows.forEach(row => {
        const lotsField = row && row.lots_label ? row.lots_label.toString() : '';
        const matches = lotsField.match(/[0-9]+/g);
        if (matches && matches.length > 0) {
          matches.forEach(item => {
            const lot = item.trim();
            if (lot !== '' && !lotAux.includes(lot)) {
              lotAux.push(lot);
            }
          });
        }
      });
      lotList = lotAux;
    }

    return lotList;
  }

  getTotalSheetsUsed(): number {
    if (!this.resultRows || this.resultRows.length === 0) {
      return 0;
    }

    return this.resultRows.reduce((acc, row) => {
      const value = Number(row && row.sheets_used !== undefined && row.sheets_used !== null ? row.sheets_used : 0);
      return acc + (isNaN(value) ? 0 : value);
    }, 0);
  }

  getTotalStacksUsed(): number {
    if (!this.resultRows || this.resultRows.length === 0) {
      return 0;
    }

    return this.resultRows.reduce((acc, row) => {
      const value = Number(row && row.stacks !== undefined && row.stacks !== null ? row.stacks : 0);
      return acc + (isNaN(value) ? 0 : value);
    }, 0);
  }

  getCompactStacksSummary(): string {
    if (this.compactStacksTotal === null || this.compactStacksTotal === undefined) {
      return 'No disponible';
    }
    return this.compactStacksTotal.toString();
  }

  setResultSort(fieldIn: string) {
    if (!fieldIn) {
      return;
    }

    if (this.resultSortField === fieldIn) {
      this.resultSortAsc = !this.resultSortAsc;
    } else {
      this.resultSortField = fieldIn;
      this.resultSortAsc = true;
    }
    this.resultPage = 1;
  }

  getResultSortMark(fieldIn: string): string {
    if (this.resultSortField !== fieldIn) {
      return '';
    }
    return this.resultSortAsc ? ' ▲' : ' ▼';
  }

  getPagedResultRows(): any[] {
    const sorted = this.getSortedResultRows();
    if (!sorted || sorted.length === 0) {
      return [];
    }

    const totalPages = this.getResultTotalPages();
    if (this.resultPage > totalPages) {
      this.resultPage = totalPages;
    }
    if (this.resultPage < 1) {
      this.resultPage = 1;
    }

    const start = (this.resultPage - 1) * this.resultPageSize;
    return sorted.slice(start, start + this.resultPageSize);
  }

  getResultTotalPages(): number {
    if (!this.resultRows || this.resultRows.length === 0) {
      return 1;
    }
    const size = this.resultPageSize > 0 ? this.resultPageSize : 10;
    return Math.max(1, Math.ceil(this.resultRows.length / size));
  }

  getResultRangeStart(): number {
    if (!this.resultRows || this.resultRows.length === 0) {
      return 0;
    }
    return ((this.resultPage - 1) * this.resultPageSize) + 1;
  }

  getResultRangeEnd(): number {
    if (!this.resultRows || this.resultRows.length === 0) {
      return 0;
    }
    const end = this.resultPage * this.resultPageSize;
    return end > this.resultRows.length ? this.resultRows.length : end;
  }

  goResultPrevPage() {
    if (this.resultPage > 1) {
      this.resultPage -= 1;
    }
  }

  goResultNextPage() {
    const totalPages = this.getResultTotalPages();
    if (this.resultPage < totalPages) {
      this.resultPage += 1;
    }
  }

  setResultPageSize(valueIn: any) {
    const size = Number(valueIn);
    if (!isNaN(size) && size > 0) {
      this.resultPageSize = size;
      this.resultPage = 1;
    }
  }

  private getSortedResultRows(): any[] {
    if (!this.resultRows || this.resultRows.length === 0) {
      return [];
    }

    const field = this.resultSortField;
    const asc = this.resultSortAsc ? 1 : -1;

    return this.resultRows.slice(0).sort((a, b) => {
      const aValue = this.getResultSortValue(a, field);
      const bValue = this.getResultSortValue(b, field);

      const aNum = Number(aValue);
      const bNum = Number(bValue);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        if (aNum < bNum) { return -1 * asc; }
        if (aNum > bNum) { return 1 * asc; }
        return 0;
      }

      const aText = (aValue === null || aValue === undefined) ? '' : aValue.toString().toLowerCase();
      const bText = (bValue === null || bValue === undefined) ? '' : bValue.toString().toLowerCase();
      if (aText < bText) { return -1 * asc; }
      if (aText > bText) { return 1 * asc; }
      return 0;
    });
  }

  private getResultSortValue(rowIn: any, fieldIn: string): any {
    if (!rowIn) {
      return '';
    }
    if (fieldIn === 'materialdesc') {
      return (rowIn.materialid ? rowIn.materialid.toString() : '') + ' ' + (rowIn.materialdesc ? rowIn.materialdesc.toString() : '');
    }
    if (fieldIn === 'a_cola' || fieldIn === 'a_resto') {
      const value = rowIn[fieldIn];
      return value === '-' || value === null || value === undefined ? -1 : Number(value);
    }
    if (fieldIn === 'waste') {
      const text = rowIn.waste ? rowIn.waste.toString().replace('%', '') : '';
      const value = Number(text);
      return isNaN(value) ? -1 : value;
    }
    if (fieldIn === 'optimization_time') {
      const seconds = Number(rowIn.optimization_time_seconds);
      return isNaN(seconds) ? 0 : seconds;
    }
    return rowIn[fieldIn];
  }
}
